<template>
  <v-card class="quick-entry">
    <v-card-title>
      Quick Entry

      <!--
        save status

        TXP-806. When `canShowSavedStatus` is false, writes to this device are failing and this
        chip must never display the word "Saved". It shows "Not saved" in the error colour
        instead. The v-if condition is deliberately unchanged, so the chip appears and disappears
        in exactly the same circumstances as before; only what it says has changed.
      -->
      <icon-value
        v-if="(!allAccordionsClosed && savedTextStatus) || (showSaveStatus && updateEncounterIndex > 0)"
        :icon="!canShowSavedStatus ? icons.mdiCloudAlert : (isUpdateComplete ? icons.mdiCheckCircleOutline : icons.mdiDotsCircle)"
        :class="(isUpdateComplete || !canShowSavedStatus) ? '' : 'spin'"
        :color="canShowSavedStatus ? 'secondary' : 'error'"
        style="width: 110px; margin-left: 10px"
      >
        {{ !canShowSavedStatus ? 'Not saved' : (savedTextStatus ? (isUpdateComplete ? 'Saved' : 'Pending...') : '') }}
      </icon-value>
      <v-spacer></v-spacer>

      <!-- clear search/filters -->
      <btn
        label="Create Quick Entry"
        :icon="icons.mdiClockPlus"
        @click="createQuickEntryModalState = true"
      ></btn>

      <!-- pre generation -->
      <btn
        label="Med Ex"
        :icon="icons.mdiLightningBolt"
        color="warning"
        @click="preGenModalState = true"
      ></btn>
    </v-card-title>

    <v-expand-transition>
      <div
        v-if="!isFiltersSet"
        style="height: 60px"
      >
        <alert class="mx-5">
          Facility and Visit Date must be selected to create a communication log.
        </alert>
      </div>
    </v-expand-transition>

    <v-expand-transition>
      <div
        v-if="encountersLength > 250"
        style="height: 60px"
      >
        <alert class="mx-5">
          Your current filter returns too many records to display. Please narrow your search by selecting a Facility and Visit Date.
        </alert>
      </div>
    </v-expand-transition>

    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="quickEntryEncounters"
      item-key="id"
      :expanded.sync="expanded"
      :page.sync=" pagination.page"
      :items-per-page="pagination.itemsPerPage"
      :sort-by.sync="sort.by"
      :sort-desc.sync="sort.desc"
      hide-default-footer
      show-select
      show-expand
      rem-single-expand
      :class="`has-pagination ${allAccordionsClosed ? 'accordion-opened' : ''}` + 'my-5'"
      @item-expanded="encounterToggleExpand"
      @current-items="getFiltered"
      @page-count="pagination.pageCount = $event"
    >
      <template #top>
        <v-row class="mx-4 mt-2 pb-1">
          <!-- first name -->
          <text-field
            v-model="search.first_name"
            :append-icon="icons.mdiMagnify"
            label="First Name"
            class="col-sm-4 px-1"
          ></text-field>
          <!-- last name -->
          <text-field
            v-model="search.last_name"
            :append-icon="icons.mdiMagnify"
            label="Last Name"
            class="col-sm-4 px-1"
          ></text-field>
          <!-- date of birth -->
          <date-input
            v-model="search.dob_us"
            :append-icon="icons.mdiMagnify"
            label="Date of Birth"
            class="col-sm-4 px-1"
            rules="dob"
          ></date-input>
        </v-row>

        <v-row class="mx-4 mt-0 pb-1">
          <!-- facility filter -->
          <auto-complete-box
            v-model="search.facility"
            :items="facilities"
            label="Facility"
            class="col-sm-6 px-1"
            :class="!search.facility && 'required-field'"
          ></auto-complete-box>
          <!-- gender filter -->
          <select-box
            v-model="search.gender"
            :items="genders"
            label="Gender"
            class="col-sm-3 px-1"
          ></select-box>
          <!-- last 4 of ssn -->
          <text-field
            v-model="search.ssn_last_4"
            :append-icon="icons.mdiMagnify"
            label="SSN"
            mask="####"
            placeholder="####"
            class="col-sm-3 px-1"
          ></text-field>
        </v-row>

        <v-row class="mx-4 mt-0 pb-1">
          <!-- visit date last 14 days -->
          <checkbox
            v-model="search.last_fourteen_days"
            class="col-sm-1p25 px-0 pl-5"
            label="Last 14"
            hide-details
            stack
          ></checkbox>
          <!-- visit date -->
          <date-picker
            v-model="search.visit_date"
            label="Visit Date"
            :max="$date().format('YYYY-MM-DD')"
            class="col-sm-3 px-1"
            :class="!search.visit_date && 'required-field'"
          ></date-picker>
          <!-- visit type filter -->
          <select-box
            v-model="search.visit_type"
            :items="visitTypes"
            label="Type of Visit"
            class="col-sm-3 px-1"
          ></select-box>
          <!-- log status -->
          <select-box
            v-model="search.logStatus"
            :items="logStatus"
            label="Log Status"
            class="col-sm-2p5 px-1"
          ></select-box>
          <select-box
            v-model="search.synced"
            :items="query"
            label="Synced"
            class="col-sm-2p25 px-1"
          ></select-box>
        </v-row>

        <v-row class="mx-4 my-4 mt-0 pb-1 d-flex flex-row-reverse">
          <btn
            label="Reset Search/Filters"
            color="secondary"
            :icon="icons.mdiUndoVariant"
            @click="resetSearchFilters"
          ></btn>
        </v-row>

        <!-- icon help -->
        <v-expand-transition>
          <div v-show="showHelp">
            <v-row class="help-icons">
              <icon-value
                :icon="icons.mdiDecagramOutline"
                color="secondary"
              >
                Validated
              </icon-value>

              <icon-value
                :icon="icons.mdiEmailOutline"
                color="secondary"
              >
                Log Status
              </icon-value>

              <icon-value
                :icon="icons.mdiCloudOutline"
                color="secondary"
              >
                Synced
              </icon-value>

              <icon-value
                :icon="icons.mdiDotsVertical"
                color="secondary"
              >
                Action Menu
              </icon-value>

              <icon-value
                :icon="icons.mdiTableRowPlusAfter"
                color="secondary"
              >
                Add Wound
              </icon-value>

              <icon-value
                :icon="icons.mdiClipboardPlusOutline"
                color="secondary"
              >
                New Treatment
              </icon-value>

              <v-icon @click="showHelp = !showHelp">
                {{ icons.mdiClose }}
              </v-icon>
            </v-row>

            <!-- Insurance status legend -->
            <v-row
              class="help-icons secondary--text"
            >
              Patient Name Underline: Insurance Status
            </v-row>

            <v-row class="help-icons secondary--text">
              <icon-value
                :icon="icons.mdiCircle"
                x-small
                color="#edb83d"
              >
                Insurance Not Specified
              </icon-value>

              <icon-value
                :icon="icons.mdiCircle"
                x-small
                color="#e16155"
              >
                Authorization Pending
              </icon-value>

              <icon-value
                :icon="icons.mdiCircle"
                x-small
                color="#7dc43c"
              >
                Cleared For Surgical Procedures
              </icon-value>
            </v-row>
          </div>
        </v-expand-transition>
      </template>

      <!-- header overrides -->
      <template #header.data-table-expand>
        <v-icon
          v-if="!allAccordionsClosed"
          :class="!allAccordionsClosed ? 'flip' : ''"
          @click="toggleAllAccordions(allAccordionsClosed)"
        >
          {{ icons.mdiChevronDoubleDown }}
        </v-icon>
      </template>
      <template #header.data-table-select>
        <v-checkbox
          v-model="selectAllStatus"
          :indeterminate="selectAllIndeterminate"
          :off-icon="icons.mdiCheckboxMultipleBlankOutline"
          :on-icon="icons.mdiCheckboxMultipleMarked"
          :indeterminate-icon="icons.mdiMinusBoxMultiple"
          hide-details
          color="secondary"
          :disabled="!isFiltersSet || !filtered.length"
          @click="selectAllToggle"
        ></v-checkbox>
      </template>
      <template #header.is_quick_entry_validated>
        <v-icon>{{ icons.mdiDecagramOutline }}</v-icon>
      </template>
      <template #header.communication_log.log_status>
        <v-icon>{{ icons.mdiEmailOutline }}</v-icon>
      </template>
      <template #header.is_synced>
        <v-icon>{{ icons.mdiCloudOutline }}</v-icon>
      </template>
      <template #header.actions>
        <v-icon
          color="warning"
          @click="showHelp = !showHelp"
        >
          {{ icons.mdiInformationOutline }}
        </v-icon>
      </template>

      <!-- table data overrides -->
      <template #item.data-table-select="{ item }">
        <v-checkbox
          v-model="selected"
          :value="item"
          hide-details
          color="secondary"
          :disabled="!isFiltersSet || !item.is_quick_entry_validated"
          @click.stop="updateSelectStatus"
        ></v-checkbox>
      </template>

      <!-- first name -->
      <template #item.first_name="{ item }">
        <div class="table-no-wrap">
          <span
            class="insurance-status"
            :class="[
              getInsuranceStatusClass(item.patient_id),
              { 'validated-name': isPatientValidatedForCommLog(item) }
            ]"
          >
            {{ item.first_name }}
          </span>
        </div>
      </template>

      <!-- last name -->
      <template #item.last_name="{ item }">
        <div class="table-no-wrap">
          <span
            class="insurance-status"
            :class="[
              getInsuranceStatusClass(item.patient_id),
              { 'validated-name': isPatientValidatedForCommLog(item) }
            ]"
          >
            {{ item.last_name }}
          </span>
        </div>
      </template>

      <!-- visit date w/edit -->
      <template #item.visit_date="{ item }">
        <!-- 20231013 JMC: Disabling this since we don't really want users to create communication logs that are not within the same day of the visit. -->
        <!--        <date-picker-->
        <!--          v-if="canEditEncounter(item)"-->
        <!--          v-model="item.visit_date"-->
        <!--          :max="$date().format('YYYY-MM-DD')"-->
        <!--          class="input-font-size-14"-->
        <!--          hide-details-->
        <!--          no-outline-->
        <!--          required-->
        <!--          style="max-width: 100px"-->
        <!--          @blur="updateTreatmentsVisitDate(item)"-->
        <!--        ></date-picker>-->
        <!--        <span v-else>-->
        <!--          {{ $date(item.visit_date).format('MM/DD/YYYY') }}-->
        <!--        </span>-->
        {{ $date(item.visit_date).format('MM/DD/YYYY') }}
      </template>

      <!-- facility w/edit -->
      <template #item.place_of_service_id="{ item }">
        <select-box
          v-if="canEditEncounter(item)"
          v-model="item.place_of_service_id"
          :items="facilities"
          class="input-font-size-14"
          clearable="false"
          hide-details
          no-outline
          not-attached
          required
          @input="updateTreatmentsFacility(item)"
        ></select-box>
        <span
          v-else
          class="table-no-wrap"
        >
          {{ $store.getters['facilities/getById'](item.place_of_service_id).title }}
        </span>
      </template>

      <!-- visit type -->
      <template #item.visit_type="{ item }">
        <span
          v-if="item.visit_type"
          class="visit-type"
          :class="item.visit_type.toLowerCase()"
        >
          {{ item.visit_type === 'Follow-up' ? 'F/U' : item.visit_type }}
        </span>
      </template>

      <!-- Validated -->
      <template #item.is_quick_entry_validated="{ item }">
        <v-icon
          :color="item.is_quick_entry_validated || item.is_validated ? 'success' : 'error'"
        >
          {{ item.is_quick_entry_validated || item.is_validated ? icons.mdiCheckDecagram : icons.mdiCloseCircle }}
        </v-icon>
      </template>

      <!-- Log status -->
      <template #item.communication_log.log_status="{ item }">
        <v-icon
          :color="logStatusColor(item.communication_log)"
        >
          {{ logStatusIcon(item.communication_log) }}
        </v-icon>
      </template>

      <!-- Synced -->
      <template #item.is_synced="{ item }">
        <v-icon
          :color="item.is_synced ? 'success' : 'error'"
        >
          {{ item.is_synced ? icons.mdiCloudCheckVariant : icons.mdiCloseCircle }}
        </v-icon>
      </template>

      <!-- actions -->
      <template #item.actions="{item}">
        <v-menu
          transition="slide-y-transition"
          offset-y
          left
        >
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>{{ attrs['aria-expanded'] === 'true' ? icons.mdiClose : icons.mdiDotsVertical }}</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item
              :disabled="!item.communication_log_id"
              @click="communicationLogDetails(item)"
            >
              <icon-value
                :disabled="!item.communication_log_id"
                :icon="icons.mdiEmailSearch"
              >
                Communication Log Details
              </icon-value>
            </v-list-item>

            <v-list-item
              :disabled="item.is_deceased || item.visit_date === today"
              @click="createQuickEntryFromEncounter(item)"
            >
              <icon-value
                :disabled="item.is_deceased || item.visit_date === today"
                :icon="icons.mdiClockPlus"
              >
                Create New Quick Entry
              </icon-value>
            </v-list-item>

            <v-divider></v-divider>

            <v-list-item @click="viewEncounter(item)">
              <icon-value :icon="icons.mdiAccountEye">
                View/Edit Encounter
              </icon-value>
            </v-list-item>

            <v-list-item @click="editPatient(item)">
              <icon-value :icon="icons.mdiAccountEdit">
                Edit This Patient
              </icon-value>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <!-- expanded data -->
      <template #expanded-item="{ item }">
        <!--
          The getPatient() method can be CPU intense, and was being accessed many times
          in the section below. So this for "loop" is a trick to only run the getPatient()
          once per encounter. There's only one item in the array, so it only renders once,
          but it allows the use of the "patient" value to be used without overhead.
        -->
        <td
          v-for="(patient) in [getPatient(item.patient_id)]"
          :key="patient.id"
          :colspan="headers.length"
          class="expanded"
          style="padding: 0 !important"
        >
          <v-expand-transition>
            <v-form
              v-if="transitioned[item.id] !== false"
              class="wounds-container"
              :disabled="!canEditEncounter(item)"
            >
              <div
                v-if="!canEditEncounter(item)"
                style="margin: 6px 6px 0 6px"
              >
                <alert
                  color="info"
                  class="col-sm-12 mb-0 py-1"
                >
                  Encounter already signed, values cannot be changed.
                </alert>
              </div>
              <!-- general patient information -->
              <v-row>
                <text-field
                  v-model="item.quick_entry_room"
                  label="Room"
                  counter="50"
                  class="col-sm-1p25"
                  hide-details
                  not-clearable
                  @keyup="updateEncounter(item, patient)"
                ></text-field>
                <text-area
                  v-model="item.quick_entry_notes"
                  label="Notes"
                  class="col-sm-5"
                  rows="1"
                  hide-details
                  not-clearable
                  @keyup="updateEncounter(item, patient)"
                ></text-area>
                <text-area
                  v-model="item.quick_entry_notes_to_nurse"
                  label="Notes to Nurse"
                  class="col-sm-5"
                  rows="1"
                  hide-details
                  not-clearable
                  @keyup="updateEncounter(item, patient)"
                ></text-area>
                <div
                  class="col-sm-0p75"
                  style="text-align: center"
                >
                  <btn
                    class="add-wound"
                    :icon="addNewWound === item.id ? icons.mdiClose : icons.mdiTableRowPlusAfter"
                    :disabled="patient.is_deceased || !canEditEncounter(item)"
                    :color="addNewWound === item.id ? 'secondary' : 'primary'"
                    @click="newWoundPracticeType = null;
                            addNewWound = addNewWound === item.id ? false : item.id"
                  ></btn>
                </div>
              </v-row>

              <v-row>
                <auto-complete-box
                  v-model="item.labs"
                  :items="labOptions"
                  label="Labs"
                  class="col-sm-8"
                  not-clearable
                  hide-details
                  multiple
                  not-attached
                  return-object
                  @change="updateEncounter(item, patient)"
                ></auto-complete-box>
                <date-picker
                  v-model="item.labs_order_date"
                  :max="$date().format('YYYY-MM-DD')"
                  label="Date Ordered"
                  class="col-sm-3p25 align-self-start"
                  clearable
                  :required="item.labs && item.labs.length > 0"
                  @input="updateEncounter(item, patient)"
                ></date-picker>
                <text-area
                  v-if="isOtherSelected(item)"
                  v-model="item.labs_other"
                  class="col-sm-11p25"
                  label="Enter other labs..."
                  rows="1"
                  required
                  @keyup="updateEncounter(item, patient)"
                ></text-area>
              </v-row>

              <v-expand-transition>
                <div
                  v-show="item.labs && item.labs.length > 0 && !item.labs_order_date"
                >
                  <alert color="error">
                    Labs order date needs to be specified if there are labs selected
                  </alert>
                </div>
              </v-expand-transition>

              <!-- Add new wound location -->
              <v-expand-transition>
                <div
                  v-if="addNewWound === item.id"
                  class="new-wound"
                >
                  <radio-buttons
                    v-model="newWoundPracticeType"
                    :items="woundTypes"
                    label="Select wound type for new wound location"
                    hide-details
                    row
                    @input="addWound(item, patient, newWoundPracticeType)"
                  ></radio-buttons>
                </div>
              </v-expand-transition>

              <v-col v-if="patient.wounds && patient.wounds.length > 0 && item.visit_type !== 'New'" class="d-flex align-center">
                <alert
                  color="primary"
                  class="mb-0 py-1 mr-3 col-sm-12"
                >
                  <div class="d-flex align-center justify-space-between w-100">
                    <span>
                      By default, only wounds treated during the last visit and new wounds are shown.
                    </span>
                    <v-switch
                      v-model="showAllWoundsMap[item.id]"
                      @change="toggleShowAllWounds(item.id, $event)"
                      label="Show All Wounds"
                      hide-details
                      class="col-sm-3p25"
                    />
                  </div>
                </alert>
              </v-col>
              <v-col class="col-12">
                <h2 class="filters-title">
                  Filter Wounds by
                </h2>
              </v-col>

              <v-col class="d-flex justify-space-between col-12">
                <select-box
                  v-model="getEncounterFilters(item.id).wound.resolved"
                  :items="['Yes', 'No']"
                  label="Resolved"
                  class="col-sm-3"
                  not-attached
                >
                </select-box>

                <select-box
                  v-model="getEncounterFilters(item.id).wound.practice_type"
                  :items="practiceTypes"
                  item-text="title"
                  item-value="id"
                  label="Wound Type"
                  class="col-sm-3"
                  multiple
                  not-attached
                >
                </select-box>

                <text-field
                  v-model="getEncounterFilters(item.id).wound.location"
                  label="Wound Location"
                  class="col-sm-3"
                  clearable
                  not-attached
                ></text-field>
              </v-col>
              <!-- wound location -->
              <div
                v-for="wound in filteredWounds(item.patient_id, item.id)"
                :key="wound.id + '-' + expandRenderKey"
                :ref="`wound-${wound.id}`"
                class="wound"
              >
                <div v-if="woundExistsNow(wound, item.visit_date, item.id)">
                  <!--
                    The latestTreatment() method can be CPU intense, and was being accessed many times
                    in the section below. So this "loop" is a trick to only run the latestTreatment()
                    once per wound. There's only one item in the array, so it only renders once, but
                    it allows the use of the "treatment" value to be used without additional overhead.
                    -->
                  <div
                    v-for="(treatment) in [latestTreatment(wound, item.visit_date, item.id)]"
                    :key="treatment.id"
                  >
                    <!-- wound header -->
                    <v-row class="wound-header">
                      <div class="col-sm-0p75 text-value">
                        <v-icon
                          v-if="!isNewTreatment(treatment, item.id) && canEditEncounter(item)"
                          class="expand-wound"
                          color="warning"
                          @click="addTreatment(item, patient, wound, treatment, wound.id)"
                        >
                          {{ icons.mdiClipboardPlusOutline }}
                        </v-icon>
                        <v-icon
                          v-if="isNewTreatment(treatment, item.id)"
                          :disabled="!treatment.is_quick_note_validated"
                          class="expand-wound"
                          @click="toggleWound(wound.id)"
                        >
                          {{ icons.mdiChevronDown }}
                        </v-icon>
                      </div>

                      <div
                        class="col-sm-2 text-value"
                      >
                        <div class="practice-pill" :class="practiceTypeClass(wound.practice_type_id)">
                          #{{ wound.wound_number }}
                          {{
                            $store.getters['baseData/practiceTypeFromId'](wound.practice_type_id).title === 'Dermatology'
                              ? 'Derm'
                              : $store.getters['baseData/practiceTypeFromId'](wound.practice_type_id).title
                          }}

                          <v-icon
                            v-if="
                              wound.practice_type_id === 1 &&
                              treatment.wound_treatment &&
                              treatment.wound_treatment.infection_signs &&
                              treatment.wound_treatment.infection_signs.some(x => x.title !== 'None')
                            "
                            color="error"
                            class="ml-1"
                          >
                            {{ icons.mdiBiohazard }}
                          </v-icon>
                        </div>
                      </div>

                      <div
                        :class="`col-sm-1p5 text-value ${
                          isNewTreatment(treatment, item.id)
                            ? (treatment.is_quick_note_validated ? 'success--text' : 'error--text')
                            : ''
                        }`"
                        :style="{ fontSize: '13px' }"
                      >
                        {{ formattedStatus(treatment) }}
                      </div>

                      <!-- wound location only for wound and derm treatments, and only editable for first treatment -->
                      <text-field
                        v-if="wound.practice_type_id !== 5 && isFirstTreatment(wound) && isNewTreatment(treatment, item.id)"
                        v-model="wound.location.location_text"
                        label="Location"
                        class="col-sm-3"
                        not-clearable
                        hide-details
                        label-top
                        maxlength="300"
                        required
                        @focus="saveTempLocation(wound)"
                        @keyup="updateEncounter(item, patient, true, wound)"
                        @click="onClickLocation(wound, item)"
                      ></text-field>

                      <!-- Show previous wound/derm treatment details -->
                      <div
                        v-if="wound.practice_type_id !== 5 && (!isFirstTreatment(wound) || !isNewTreatment(treatment, item.id))"
                        class="col-sm-3 text-value"
                      >
                        {{ formattedLocation(wound) }}
                      </div>

                      <!-- etiology only for wound and derm treatments -->
                      <auto-complete-box
                        v-if="wound.practice_type_id !== 5 && isNewTreatment(treatment, item.id)"
                        v-model="treatment.etiology"
                        :items="wound.practice_type_id === 1 ? woundEtiologies : dermEtiologies"
                        label="Etiology"
                        item-value="title"
                        clearable="false"
                        class="col-sm-4"
                        dense
                        hide-details
                        label-top
                        not-attached
                        required
                        :ref="`etiology-${wound.id}`"
                        @change="onEtiologyChange($event, wound.id, treatment.id, item, patient)"
                      ></auto-complete-box>

                      <!-- show previous wound/derm treatment details -->
                      <div
                        v-if="wound.practice_type_id !== 5 && !isNewTreatment(treatment, item.id)"
                        class="col-sm-4 text-value"
                      >
                        {{ treatment.etiology }}
                        <span v-if="treatment.etiology === 'Other'">
                          - {{ treatment.etiology_other }}
                        </span>
                        <div
                          v-if="wound.practice_type_id === 1
                            && treatment.wound_treatment
                            && treatment.wound_treatment.wound_treatment_wound_size"
                        >
                          {{ treatment.wound_treatment.wound_treatment_wound_size.wound_size_text }}
                        </div>
                        <div
                          v-if="wound.practice_type_id"
                          class="no-wrap"
                        >
                          {{ $store.getters['baseData/procedureFromId'](treatment.procedure_id) }}
                        </div>
                      </div>

                      <!-- g-tube treatments -->
                      <auto-complete-box
                        v-if="wound.practice_type_id === 5
                          && treatment.g_tube_treatment
                          && isNewTreatment(treatment, item.id)"
                        v-model="treatment.g_tube_treatment.abdominal_exam"
                        :items="abdominalExams"
                        label="Abdominal Exam"
                        :class="treatment.g_tube_treatment.abdominal_exam === 'Other' ? 'col-sm-2p25' : 'col-sm-7'"
                        clearable="false"
                        hide-details
                        label-top
                        not-attached
                        @change="updateEncounterImmediate(item, patient)"
                      ></auto-complete-box>
                      <text-field
                        v-if="wound.practice_type_id === 5
                          && treatment.g_tube_treatment
                          && treatment.g_tube_treatment.abdominal_exam === 'Other'
                          && isNewTreatment(treatment, item.id)"
                        v-model="treatment.g_tube_treatment.abdominal_exam_other"
                        label="Other Abdominal Exam"
                        class="col-sm-4p75"
                        maxlength="1700"
                        not-clearable
                        hide-details
                        label-top
                        @keyup="updateEncounter(item, patient)"
                      ></text-field>

                      <!-- Show previous g-tube treatment details -->
                      <div
                        v-if="wound.practice_type_id === 5
                          && treatment.g_tube_treatment
                          && !isNewTreatment(treatment, item.id)"
                        class="col-sm-3 text-value"
                      >
                        {{ treatment.g_tube_treatment.abdominal_exam }}
                        <span v-if="treatment.g_tube_treatment.abdominal_exam === 'Other'">
                          - {{ treatment.g_tube_treatment.abdominal_exam_other }}
                        </span>
                      </div>
                      <div
                        v-if="wound.practice_type_id === 5
                          && treatment.g_tube_treatment
                          && !isNewTreatment(treatment, item.id)"
                        class="col-sm-4 text-value"
                      >
                        {{ treatment.g_tube_treatment.replacement_tube_type }}
                        <span v-if="treatment.g_tube_treatment.balloon_cc">
                          / {{ treatment.g_tube_treatment.balloon_cc }}
                        </span>
                        <span v-if="treatment.g_tube_treatment.replacement_size">
                          / {{ treatment.g_tube_treatment.replacement_size }}
                        </span>
                        <div
                          v-if="wound.practice_type_id === 5"
                          class="no-wrap"
                        >
                          {{ $store.getters['baseData/procedureFromId'](treatment.procedure_id) }}
                        </div>
                      </div>

                      <!-- Wound specific menu -->
                      <div
                        class="col-sm-0p75"
                        style="text-align: center"
                      >
                        <v-menu
                          transition="slide-y-transition"
                          offset-y
                          left
                        >
                          <template #activator="{ on, attrs }">
                            <v-btn
                              icon
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon>{{ attrs['aria-expanded'] === 'true' ? icons.mdiClose : icons.mdiDotsVertical }}</v-icon>
                            </v-btn>
                          </template>
                          <v-list>
                            <!-- View wound history -->
                            <v-list-item
                              :disabled="wound.practice_type_id !== 1 && wound.practice_type_id !== 6"
                              @click="openWoundHxModal(wound)"
                            >
                              <icon-value
                                :icon="icons.mdiHistory"
                              >
                                View History
                              </icon-value>
                            </v-list-item>

                            <!-- Delete treatment/wound -->
                            <v-list-item
                              v-if="canEditEncounter(item) && isNewTreatment(treatment, item.id)"
                              @click="deleteTreatment(item, patient, wound, treatment)"
                            >
                              <icon-value
                                :icon="icons.mdiTrashCan"
                                color="error"
                              >
                                Delete Entry?
                              </icon-value>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </v-row>

                    <v-expand-transition>
                      <text-area
                        v-if="treatment.etiology === 'Other' && isNewTreatment(treatment, item.id)"
                        v-model="treatment.etiology_other"
                        label="Other Etiology"
                        class="etiology-other col-sm-6p75"
                        not-clearable
                        hide-details
                        label-top
                        required
                        rows="1"
                        maxlength="900"
                        counter="900"
                        :ref="`etiology-other-${wound.id}`"
                        @keyup="onEtiologyChange(treatment.etiology_other, wound.id, treatment.id, item, patient)"
                      ></text-area>
                    </v-expand-transition>

                    <!-- wound details -->
                    <div
                      v-if="wound.practice_type_id === 1 && treatment.wound_treatment && treatment.wound_treatment.wound_treatment_wound_size"
                      class="wound-details"
                      :ref="`wound-details-${wound.id}`"
                    >

                      <v-expand-transition>
                        <div
                          v-show="(validatePrimaryDims(treatment.wound_treatment.wound_treatment_wound_size) || validatePostDebridDims(treatment.wound_treatment.wound_treatment_wound_size))"
                        >
                          <alert color="warning">
                            ALL dimension fields must be populated or cleared.
                          </alert>
                        </div>
                      </v-expand-transition>

                      <div class="wound-container">
                        <div class="px-1">
                          <alert
                            v-if="wound.is_healed"
                            color="info"
                          >
                            This wound was marked as resolved in the encounter details page and its measurements can't be modified.
                          </alert>
                        </div>

                        <v-card-actions>
                          <text-field
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.primary_length"
                            label="Pre Length"
                            numeric
                            :mask="measurementMask"
                            suffix="cm"
                            rules="numberRange(0,100)"
                            not-clearable
                            hide-details
                            label-top
                            :required="validatePrimaryDims(treatment.wound_treatment.wound_treatment_wound_size)"
                            :disabled="wound.is_healed"
                            @blur="treatment.wound_treatment.wound_treatment_wound_size.primary_length = treatment.wound_treatment.wound_treatment_wound_size.primary_length ? Number(treatment.wound_treatment.wound_treatment_wound_size.primary_length).toFixed(1) : null"
                            @keyup="calcWoundSize(treatment, item, patient)"
                          ></text-field>
                          <text-field
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.primary_width"
                            label="Pre Width"
                            numeric
                            :mask="measurementMask"
                            suffix="cm"
                            rules="numberRange(0,100)"
                            not-clearable
                            hide-details
                            label-top
                            :required="validatePrimaryDims(treatment.wound_treatment.wound_treatment_wound_size)"
                            :disabled="wound.is_healed"
                            @blur="treatment.wound_treatment.wound_treatment_wound_size.primary_width = treatment.wound_treatment.wound_treatment_wound_size.primary_width ? Number(treatment.wound_treatment.wound_treatment_wound_size.primary_width).toFixed(1) : null"
                            @keyup="calcWoundSize(treatment, item, patient)"
                          ></text-field>
                          <text-field
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.primary_depth"
                            label="Pre Depth cm"
                            numeric
                            allow-utd
                            :mask="treatment.wound_treatment.wound_treatment_wound_size.primary_depth !== 'UTD' ? measurementMask : false"
                            rules="numberRangeUTD(0,100)"
                            :append-icon="treatment.wound_treatment.wound_treatment_wound_size.primary_depth !== 'UTD' ? icons.mdiTimelineQuestion : null"
                            :not-clearable="treatment.wound_treatment.wound_treatment_wound_size.primary_depth !== 'UTD'"
                            hide-details
                            label-top
                            :required="validatePrimaryDims(treatment.wound_treatment.wound_treatment_wound_size)"
                            :disabled="wound.is_healed"
                            @click:append="setWoundSizePrimaryDepth(treatment, 'UTD', item, patient)"
                            @click:clear="setWoundSizePrimaryDepth(treatment, null, item, patient)"
                            @blur="woundSizePrimaryDepth(treatment)"
                            @keyup="calcWoundSize(treatment, item, patient)"
                          ></text-field>
                          <div>&nbsp;&nbsp;</div>
                          <text-field
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.post_debridement_length"
                            label="Post Length"
                            numeric
                            :mask="measurementMask"
                            suffix="cm"
                            rules="numberRange(0,100)"
                            not-clearable
                            hide-details
                            label-top
                            :required="validatePostDebridDims(treatment.wound_treatment.wound_treatment_wound_size)"
                            :disabled="wound.is_healed || (isProcedureDebrid(treatment) || !treatment.procedure_id ? false : true)"
                            @blur="treatment.wound_treatment.wound_treatment_wound_size.post_debridement_length = treatment.wound_treatment.wound_treatment_wound_size.post_debridement_length ? Number(treatment.wound_treatment.wound_treatment_wound_size.post_debridement_length).toFixed(1) : null"
                            @keyup="calcDebridArea(treatment); updateEncounter(item, patient)"
                          ></text-field>
                          <text-field
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.post_debridement_width"
                            label="Post Width"
                            numeric
                            :mask="measurementMask"
                            suffix="cm"
                            rules="numberRange(0,100)"
                            not-clearable
                            hide-details
                            label-top
                            :required="validatePostDebridDims(treatment.wound_treatment.wound_treatment_wound_size)"
                            :disabled="wound.is_healed || (isProcedureDebrid(treatment) || !treatment.procedure_id ? false : true)"
                            @blur="treatment.wound_treatment.wound_treatment_wound_size.post_debridement_width = treatment.wound_treatment.wound_treatment_wound_size.post_debridement_width ? Number(treatment.wound_treatment.wound_treatment_wound_size.post_debridement_width).toFixed(1) : null"
                            @keyup="calcDebridArea(treatment); updateEncounter(item, patient)"
                          ></text-field>
                          <text-field
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.post_debridement_depth"
                            label="Post Depth cm"
                            numeric
                            allow-utd
                            :mask="treatment.wound_treatment.wound_treatment_wound_size.post_debridement_depth !== 'UTD' ? measurementMask : false"
                            rules="numberRangeUTD(0,100)"
                            :append-icon="treatment.wound_treatment.wound_treatment_wound_size.post_debridement_depth !== 'UTD' ? icons.mdiTimelineQuestion : null"
                            :not-clearable="treatment.wound_treatment.wound_treatment_wound_size.post_debridement_depth !== 'UTD'"
                            hide-details
                            label-top
                            :required="validatePostDebridDims(treatment.wound_treatment.wound_treatment_wound_size)"
                            :disabled="wound.is_healed || (isProcedureDebrid(treatment) || !treatment.procedure_id ? false : true)"
                            @click:append="setWoundSizePostDepth(treatment, 'UTD', item, patient)"
                            @click:clear="setWoundSizePostDepth(treatment, null, item, patient)"
                            @blur="woundSizePostDepth(treatment)"
                            @keyup="updateEncounter(item, patient)"
                          ></text-field>
                          <!--                           20231004 JMC: Disabling this for now since this is not in the provided template.-->
                          <!--                          <text-field-->
                          <!--                            v-model="treatment.wound_treatment.wound_treatment_wound_size.post_debridement_percent"-->
                          <!--                            label="Debridement"-->
                          <!--                            numeric-->
                          <!--                            suffix="%"-->
                          <!--                            mask="###"-->
                          <!--                            rules="numberRange(1,100)"-->
                          <!--                            not-clearable-->
                          <!--                            hide-details-->
                          <!--                            label-top-->
                          <!--                            @keyup="calcDebridArea(treatment); updateEncounter(item, patient)"-->
                          <!--                          ></text-field>-->
                        </v-card-actions>

                        <v-card-actions>
                          <text-field
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.slough"
                            label="Slough"
                            numeric
                            mask="###"
                            rules="numberRange(0,100)"
                            suffix="%"
                            not-clearable
                            hide-details
                            label-top
                            :disabled="wound.is_healed"
                            @blur="treatment.wound_treatment.wound_treatment_wound_size.slough = clampTissuePercent(treatment.wound_treatment.wound_treatment_wound_size.slough)"
                            @keyup="updateEncounter(item, patient)"
                          ></text-field>
                          <text-field
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.granulation"
                            label="Granulation"
                            numeric
                            mask="###"
                            rules="numberRange(0,100)"
                            suffix="%"
                            not-clearable
                            hide-details
                            label-top
                            :disabled="wound.is_healed"
                            @blur="treatment.wound_treatment.wound_treatment_wound_size.granulation = clampTissuePercent(treatment.wound_treatment.wound_treatment_wound_size.granulation)"
                            @keyup="updateEncounter(item, patient)"
                          ></text-field>
                          <text-field
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.necrotic_tissue"
                            label="Necrotic"
                            numeric
                            mask="###"
                            rules="numberRange(0,100)"
                            suffix="%"
                            not-clearable
                            hide-details
                            label-top
                            :disabled="wound.is_healed"
                            @blur="treatment.wound_treatment.wound_treatment_wound_size.necrotic_tissue = clampTissuePercent(treatment.wound_treatment.wound_treatment_wound_size.necrotic_tissue)"
                            @keyup="updateEncounter(item, patient)"
                          ></text-field>
                          <text-field
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.hyper_granulation"
                            label="Hyper granulation"
                            numeric
                            mask="###"
                            rules="numberRange(0,100)"
                            suffix="%"
                            not-clearable
                            hide-details
                            label-top
                            :disabled="wound.is_healed"
                            @blur="treatment.wound_treatment.wound_treatment_wound_size.hyper_granulation = clampTissuePercent(treatment.wound_treatment.wound_treatment_wound_size.hyper_granulation)"
                            @keyup="updateEncounter(item, patient)"
                          ></text-field>
                          <text-field
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.eschar"
                            label="Eschar"
                            numeric
                            rules="numberRange(0,100)"
                            mask="###"
                            suffix="%"
                            not-clearable
                            hide-details
                            label-top
                            :disabled="wound.is_healed"
                            @blur="treatment.wound_treatment.wound_treatment_wound_size.eschar = clampTissuePercent(treatment.wound_treatment.wound_treatment_wound_size.eschar)"
                            @keyup="updateEncounter(item, patient)"
                          ></text-field>
                          <text-field
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.epithelial"
                            label="Epithelial"
                            numeric
                            rules="numberRange(0,100)"
                            mask="###"
                            suffix="%"
                            not-clearable
                            hide-details
                            label-top
                            :disabled="wound.is_healed"
                            @blur="treatment.wound_treatment.wound_treatment_wound_size.epithelial = clampTissuePercent(treatment.wound_treatment.wound_treatment_wound_size.epithelial)"
                            @keyup="updateEncounter(item, patient)"
                          ></text-field>
                          <text-field
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.dermal"
                            label="Dermal"
                            numeric
                            mask="###"
                            rules="numberRange(0,100)"
                            suffix="%"
                            not-clearable
                            hide-details
                            label-top
                            :disabled="wound.is_healed"
                            @blur="treatment.wound_treatment.wound_treatment_wound_size.dermal = clampTissuePercent(treatment.wound_treatment.wound_treatment_wound_size.dermal)"
                            @keyup="updateEncounter(item, patient)"
                          ></text-field>
                        </v-card-actions>
                        <v-card-actions>
                          <select-box
                            v-if="isEtiologyPressure(treatment)"
                            v-model="treatment.wound_treatment.wound_treatment_wound_size.wound_stage"
                            :menu-props="{ offsetY: true, closeOnClick: true, maxHeight: '200px' }"
                            :items="woundStages"
                            class="col-sm-12"
                            label="Wound Stage (NPUAP 4+2)"
                            required
                            dense
                            hide-details
                            label-top
                            not-attached
                          >
                          </select-box>
                        </v-card-actions>

                        <v-row>
                          <auto-complete-box
                            v-model="treatment.procedure_id"
                            :items="woundProcedures"
                            label="Procedure Performed"
                            class="col-sm-6 fix-overflow"
                            dense
                            hide-details
                            label-top
                            not-attached
                            @change="validateProcedure(treatment);updateEncounter(item, patient)"
                          ></auto-complete-box>
                          <auto-complete-box
                            v-model="treatment.wound_treatment.infection_signs"
                            :items="infectionSigns"
                            label="Signs of Infection"
                            class="col-sm-6 fix-overflow"
                            dense
                            multiple
                            hide-details
                            label-top
                            return-object
                            not-attached
                            distinctive-none
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                        </v-row>
                        <v-row style="margin-top: 6px !important">
                          <auto-complete-box
                            :value="Array.isArray(treatment.wound_treatment.exudate_amounts)
                                    ? treatment.wound_treatment.exudate_amounts[0]
                                    : treatment.wound_treatment.exudate_amounts"
                            @input="handleExudateInput($event, treatment, item, patient)"
                            :items="exudateAmounts"
                            label="Exudate Amount"
                            class="col-sm-6 fix-overflow mt--2"
                            dense
                            hide-details
                            label-top
                            return-object
                            not-attached
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                          <auto-complete-box
                            v-model="treatment.wound_treatment.exudate_types"
                            :items="exudateTypes"
                            label="Exudate Types"
                            class="col-sm-6 fix-overflow mt--2"
                            dense
                            multiple
                            hide-details
                            label-top
                            return-object
                            not-attached
                            :disabled="isExudateAmountEmpty(treatment)"
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                        </v-row>
                        <v-row>
                          <text-area
                            v-model="treatment.wound_treatment.qent_undermining"
                            label="Undermining"
                            class="col-sm-12"
                            rows="1"
                            not-clearable
                            hide-details
                            label-top
                            maxlength="255"
                            @keyup="updateEncounter(item, patient)"
                          ></text-area>
                        </v-row>

                        <v-row>
                          <auto-complete-box
                              v-model="treatment.wound_treatment.progress"
                              :items="progresses"
                              label="Wound Progress"
                              :labelTop="true"
                              class="col-sm-6 mb--5"
                              @change="updateWoundProgress(item, patient)"
                            >
                            </auto-complete-box>

                            <text-area
                              :disabled="treatment.wound_treatment.progress !== 'Other'"
                              v-model="treatment.wound_treatment.progress_other"
                              label="Enter other wound progress..."
                              :labelTop="true"
                              class="col-sm-6"
                              required
                              counter="2200"
                              maxlength="2200"
                              rows="1"
                              :hideDetails=true
                              @input="updateEncounter(item, patient)"
                            ></text-area>
                        </v-row>

                        <v-row>
                          <auto-complete-box
                            v-if="treatment.operative_note_wizard"
                            v-model="treatment.operative_note_wizard.consent_selected"
                            :items="consentOptions"
                            label="Consent"
                            class="col-sm-3p5"
                            dense
                            hide-details
                            label-top
                            not-attached
                            @change="consentChanged(item, patient, treatment)"
                          ></auto-complete-box>
                          <text-area
                            v-model="treatment.wound_treatment.wound_description_summary"
                            label="Wound Description"
                            class="col-sm-8p5"
                            rows="1"
                            not-clearable
                            hide-details
                            label-top
                            maxlength="8500"
                            counter="8500"
                            @keyup="updateEncounter(item, patient)"
                          ></text-area>
                        </v-row>

                        <v-row>
                          <auto-complete-box
                            v-model="treatment.wound_treatment.dressing_used_1"
                            :items="dressings"
                            label="Dressings Used"
                            class="col-sm-6 fix-overflow"
                            dense
                            hide-details
                            label-top
                            not-attached
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                          <auto-complete-box
                            v-model="treatment.wound_treatment.dressing_used_2"
                            :items="dressings"
                            label="Additional Dressing Used"
                            class="col-sm-6 fix-overflow"
                            dense
                            hide-details
                            label-top
                            not-attached
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                        </v-row>
                        <v-row>
                          <text-area
                            v-model="treatment.wound_treatment.dressing_used_other"
                            label="Enter Other Dressings Used"
                            class="col-sm-6p5"
                            rows="1"
                            not-clearable
                            hide-details
                            label-top
                            @keyup="updateEncounter(item, patient)"
                          ></text-area>
                        </v-row>

                        <v-row class="only-comm-log">
                          <div class="col-12">
                            <v-row>
                              <div class="col-12 mb-1">
                                Fields marked with * only appear on communication log and nowhere on the actual note!
                              </div>
                            </v-row>
                            <v-row>
                              <text-area
                                v-model="treatment.qent_recommendation"
                                label="Recommendation*"
                                class="col-sm-12"
                                rows="1"
                                not-clearable
                                hide-details
                                label-top
                                maxlength="255"
                                @keyup="updateEncounter(item, patient)"
                              ></text-area>
                            </v-row>
                            <v-row>
                              <text-area
                                v-model="treatment.qent_notes"
                                label="Notes*"
                                class="col-sm-6p5"
                                rows="1"
                                not-clearable
                                hide-details
                                label-top
                                @keyup="updateEncounter(item, patient)"
                              ></text-area>
                              <text-area
                                v-model="treatment.current_treatment"
                                label="Current Treatment*"
                                class="col-sm-6p5"
                                rows="1"
                                not-clearable
                                hide-details
                                label-top
                                @keyup="updateEncounter(item, patient)"
                              ></text-area>
                            </v-row>
                          </div>
                        </v-row>
                      </div>
                    </div>

                    <!-- derm details -->
                    <div
                      v-if="wound.practice_type_id === 6 && treatment.derm_treatment"
                      class="wound-details"
                      :ref="`wound-details-${wound.id}`"
                    >
                      <div class="wound-container">
                        <v-row>
                          <auto-complete-box
                            v-model="treatment.derm_treatment.lesion_conditions"
                            :items="lesionConditions"
                            label="Lesion Conditions"
                            class="col-sm-6 fix-overflow"
                            dense
                            multiple
                            hide-details
                            label-top
                            return-object
                            not-attached
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                          <text-field
                            v-if="treatment.derm_treatment.lesion_conditions ? treatment.derm_treatment.lesion_conditions.some(x => x.title === 'Other') : false"
                            v-model="treatment.derm_treatment.lesion_conditions_other"
                            label="Other lesion conditions..."
                            maxlength="1500"
                            hide-details
                            label-top
                            class="col-sm-6"
                          ></text-field>
                        </v-row>
                        <v-row>
                          <auto-complete-box
                            v-model.trim="treatment.derm_treatment.topical_agent"
                            :items="topicalAgents"
                            item-value="title"
                            item-text="title"
                            label="Topical Agent"
                            class="col-sm-7"
                            :clearable="treatment.derm_treatment.secondary_topical_agent ? 'false' : null"
                            hide-details
                            label-top
                            not-attached
                            @click:clear="treatment.derm_treatment.topical_agent_strength = null"
                            @change="updateEncounter(item, patient)"
                            allow-custom-values
                            :max-length="50"
                          ></auto-complete-box>
                          <text-field
                            v-model="treatment.derm_treatment.topical_agent_strength"
                            class="col-sm-5"
                            not-clearable
                            hide-details
                            label-top
                            label="Topical Agent Strength"
                            :placeholder="!treatment.derm_treatment.topical_agent ? 'Select agent to specify strength' : ''"
                            :disabled="!treatment.derm_treatment.topical_agent"
                            @keyup="updateEncounter(item, patient)"
                            maxlength="255"
                            counter="255"
                          ></text-field>
                        </v-row>

                        <v-expand-transition>
                          <v-row v-if="treatment.derm_treatment.topical_agent">
                            <auto-complete-box
                              v-model.trim="treatment.derm_treatment.secondary_topical_agent"
                              :items="topicalAgents"
                              item-value="title"
                              item-text="title"
                              label="Secondary Topical Agent (optional)"
                              class="col-sm-7"
                              hide-details
                              label-top
                              not-attached
                              @click:clear="treatment.derm_treatment.secondary_topical_agent_strength = null"
                              @change="updateEncounter(item, patient)"
                              allow-custom-values
                              :max-length="50"
                            ></auto-complete-box>
                            <text-field
                              v-model="treatment.derm_treatment.secondary_topical_agent_strength"
                              class="col-sm-5"
                              not-clearable
                              hide-details
                              label-top
                              label="Secondary Topical Agent Strength"
                              :placeholder="!treatment.derm_treatment.secondary_topical_agent ? 'Select agent to specify strength' : ''"
                              :disabled="!treatment.derm_treatment.secondary_topical_agent"
                              @keyup="updateEncounter(item, patient)"
                              maxlength="255"
                              counter="255"
                            ></text-field>
                          </v-row>
                        </v-expand-transition>

                        <v-row>
                          <text-area
                            v-model="treatment.derm_treatment.lesion_description"
                            label="Lesion Description"
                            class="col-sm-6"
                            rows="1"
                            not-clearable
                            hide-details
                            label-top
                            @keyup="updateEncounter(item, patient)"
                          ></text-area>
                          <select-box
                            v-model="treatment.derm_treatment.duration"
                            :items="durations"
                            label="Duration"
                            class="col-sm-6"
                            hide-details
                            label-top
                            not-attached
                            dense
                            @change="updateEncounter(item, patient)"
                          ></select-box>
                        </v-row>

                        <v-row>
                          <auto-complete-box
                            v-model="treatment.procedure_id"
                            :items="dermProcedures"
                            label="Procedure Performed"
                            class="col-sm-12"
                            dense
                            hide-details
                            label-top
                            not-attached
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                        </v-row>

                        <v-row class="only-comm-log">
                          <div class="col-12">
                            <v-row>
                              <div class="col-12 mb-1">
                                Fields marked with * only appear on communication log and nowhere on the actual note!
                              </div>
                            </v-row>
                            <v-row>
                              <text-area
                                v-model="treatment.qent_recommendation"
                                label="Recommendation*"
                                class="col-sm-12"
                                rows="1"
                                not-clearable
                                hide-details
                                label-top
                                maxlength="255"
                                @keyup="updateEncounter(item, patient)"
                              ></text-area>
                            </v-row>
                            <v-row>
                              <text-area
                                v-model="treatment.qent_notes"
                                label="Notes*"
                                class="col-sm-6p5"
                                rows="1"
                                not-clearable
                                hide-details
                                label-top
                                @keyup="updateEncounter(item, patient)"
                              ></text-area>
                              <text-area
                                v-model="treatment.current_treatment"
                                label="Current Treatment*"
                                class="col-sm-6p5"
                                rows="1"
                                not-clearable
                                hide-details
                                label-top
                                @keyup="updateEncounter(item, patient)"
                              ></text-area>
                            </v-row>
                          </div>
                        </v-row>
                      </div>
                    </div>

                    <!-- g-tube details -->
                    <div
                      v-if="wound.practice_type_id === 5 && treatment.g_tube_treatment"
                      class="wound-details"
                      :ref="`wound-details-${wound.id}`"
                    >
                      <div class="wound-container">
                        <v-row>
                          <auto-complete-box
                            v-model="treatment.g_tube_treatment.g_tube_type"
                            :items="gTubeTypes"
                            label="G-Tube Type"
                            :class="treatment.g_tube_treatment.g_tube_type === 'Other' ? 'col-sm-3' : 'col-sm-4'"
                            hide-details
                            label-top
                            not-attached
                            @click:clear="
                              treatment.g_tube_treatment.g_tube_type_other = null;
                              treatment.g_tube_treatment.peri_tube_finding = null;
                              treatment.g_tube_treatment.peri_tube_tract = null"
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                          <text-field
                            v-if="treatment.g_tube_treatment.g_tube_type === 'Other'"
                            v-model="treatment.g_tube_treatment.g_tube_type_other"
                            label="Other G-tube Type"
                            class="col-sm-3"
                            not-clearable
                            hide-details
                            label-top
                            @keyup="updateEncounter(item, patient)"
                            maxlength="255"
                            counter="255"
                          ></text-field>
                          <auto-complete-box
                            v-model="treatment.g_tube_treatment.peri_tube_finding"
                            :items="periTubeFindings"
                            label="Peri Tube Finding"
                            :class="treatment.g_tube_treatment.g_tube_type === 'Other' ? 'col-sm-3' : 'col-sm-4'"
                            clearable="false"
                            hide-details
                            label-top
                            not-attached
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                          <auto-complete-box
                            v-model="treatment.g_tube_treatment.peri_tube_tract"
                            :items="periTubeTracts"
                            label="Peri Tube Tract"
                            :class="treatment.g_tube_treatment.g_tube_type === 'Other' ? 'col-sm-3' : 'col-sm-4'"
                            clearable="false"
                            hide-details
                            label-top
                            not-attached
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                        </v-row>

                        <v-row>
                          <auto-complete-box
                            v-model="treatment.g_tube_treatment.replacement_tube_type"
                            :items="gTubeTypes"
                            label="Repl. Tube Type"
                            :class="treatment.g_tube_treatment.replacement_tube_type === 'Other' ? 'col-sm-3' : 'col-sm-4'"
                            hide-details
                            label-top
                            not-attached
                            @click:clear="
                              treatment.g_tube_treatment.replacement_tube_type_other = null;
                              treatment.g_tube_treatment.balloon_cc = null;
                              treatment.g_tube_treatment.replacement_size = null"
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                          <text-field
                            v-if="treatment.g_tube_treatment.replacement_tube_type === 'Other'"
                            v-model="treatment.g_tube_treatment.replacement_tube_type_other"
                            label="Other Replacement Tube Type"
                            class="col-sm-3"
                            not-clearable
                            hide-details
                            maxlength="255"
                            counter="255"
                            label-top
                            @keyup="updateEncounter(item, patient)"
                          ></text-field>
                          <auto-complete-box
                            v-model="treatment.g_tube_treatment.balloon_cc"
                            :items="balloonCc"
                            label="Repl. Balloon Size (cc)"
                            :class="treatment.g_tube_treatment.replacement_tube_type === 'Other' ? 'col-sm-3' : 'col-sm-4'"
                            clearable="false"
                            hide-details
                            label-top
                            not-attached
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                          <auto-complete-box
                            v-model="treatment.g_tube_treatment.replacement_size"
                            :items="replacementSize"
                            label="Repl. Tube Size (Fr)"
                            :class="treatment.g_tube_treatment.replacement_tube_type === 'Other' ? 'col-sm-3' : 'col-sm-4'"
                            clearable="false"
                            hide-details
                            label-top
                            not-attached
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                        </v-row>

                        <v-row>
                          <auto-complete-box
                            v-model="treatment.g_tube_treatment.replacement_reason"
                            :items="replacementReasons"
                            label="Replacement Reason"
                            class="col-sm-6"
                            hide-details
                            label-top
                            not-attached
                            dense
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                          <auto-complete-box
                            v-model="treatment.g_tube_treatment.duration"
                            :items="durations"
                            label="Duration"
                            class="col-sm-6"
                            hide-details
                            label-top
                            not-attached
                            dense
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                        </v-row>

                        <v-expand-transition>
                          <v-row v-if="treatment.g_tube_treatment.replacement_reason === 'Other'">
                            <text-area
                              v-model="treatment.g_tube_treatment.replacement_reason_other"
                              label="Other replacement reason"
                              class="col-sm-12"
                              rows="1"
                              hide-details
                              label-top
                              @keyup="updateEncounter(item, patient)"
                            ></text-area>
                          </v-row>
                        </v-expand-transition>

                        <v-row>
                          <auto-complete-box
                            v-model="treatment.procedure_id"
                            :items="gtubeProcedures"
                            label="Procedure Performed"
                            class="col-sm-12"
                            hide-details
                            label-top
                            not-attached
                            dense
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                        </v-row>

                        <v-row>
                          <auto-complete-box
                            v-model="treatment.g_tube_treatment.verification_of_placement_1"
                            :items="verificationOfPlacements"
                            label="Verification of Placement"
                            class="col-sm-12 hundred-percent"
                            hide-details
                            label-top
                            not-attached
                            dense
                            @change="updateEncounter(item, patient)"
                          ></auto-complete-box>
                        </v-row>

                        <v-expand-transition>
                          <v-row v-if="treatment.g_tube_treatment.verification_of_placement_1">
                            <auto-complete-box
                              v-model="treatment.g_tube_treatment.verification_of_placement_2"
                              :items="verificationOfPlacements"
                              label="Secondary Verification of Placement (optional)"
                              class="col-sm-6"
                              hide-details
                              label-top
                              not-attached
                              dense
                              @change="updateEncounter(item, patient)"
                            ></auto-complete-box>
                            <auto-complete-box
                              v-model="treatment.g_tube_treatment.verification_of_placement_3"
                              :items="verificationOfPlacements"
                              label="Tertiary Verification of Placement (optional)"
                              class="col-sm-6"
                              hide-details
                              label-top
                              not-attached
                              dense
                              @change="updateEncounter(item, patient)"
                            ></auto-complete-box>
                          </v-row>
                        </v-expand-transition>

                        <v-expand-transition>
                          <v-row
                            v-if="treatment.g_tube_treatment.verification_of_placement_1 === 'Other'
                              || treatment.g_tube_treatment.verification_of_placement_2 === 'Other'
                              || treatment.g_tube_treatment.verification_of_placement_3 === 'Other'"
                          >
                            <text-area
                              v-model="treatment.g_tube_treatment.verification_of_placement_other"
                              label="Other verification of placement..."
                              class="col-sm-12"
                              rows="1"
                              hide-details
                              label-top
                              @keyup="updateEncounter(item, patient)"
                            ></text-area>
                          </v-row>
                        </v-expand-transition>

                         <!-- operative_note -->
                         <v-expand-transition>
                          <v-row>
                            <text-area
                              v-model="treatment.operative_note"
                              label="Procedural Note"
                              class="col-sm-12"
                              rows="1"
                              maxlength="8300"
                              counter="8300"
                              hide-details
                              @keyup="updateEncounter(item, patient)"
                            ></text-area>
                          </v-row>
                        </v-expand-transition>

                        <v-row class="only-comm-log">
                          <div class="col-12">
                            <v-row>
                              <div class="col-12 mb-1">
                                Fields marked with * only appear on communication log and nowhere on the actual note!
                              </div>
                            </v-row>
                            <v-row>
                              <text-area
                                v-model="treatment.qent_recommendation"
                                label="Recommendation*"
                                class="col-sm-12"
                                rows="1"
                                not-clearable
                                hide-details
                                label-top
                                maxlength="255"
                                @keyup="updateEncounter(item, patient)"
                              ></text-area>
                            </v-row>
                            <v-row>
                              <text-area
                                v-model="treatment.qent_notes"
                                label="Notes*"
                                class="col-sm-6p5"
                                rows="1"
                                not-clearable
                                hide-details
                                label-top
                                @keyup="updateEncounter(item, patient)"
                              ></text-area>
                              <text-area
                                v-model="treatment.current_treatment"
                                label="Current Treatment*"
                                class="col-sm-6p5"
                                rows="1"
                                not-clearable
                                hide-details
                                label-top
                                @keyup="updateEncounter(item, patient)"
                              ></text-area>
                            </v-row>
                          </div>
                        </v-row>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-form>
          </v-expand-transition>
        </td>
      </template>
    </v-data-table>

    <!-- pagination -->
    <v-expand-transition>
      <div
        style="height: 78px"
      >
        <pagination
          :page.sync="pagination.page"
          :items-per-page.sync="pagination.itemsPerPage"
          :page-count="pagination.pageCount"
          :items-per-page-list="pagination.itemsPerPageList"
        ></pagination>
      </div>
    </v-expand-transition>

    <div
      v-if="isFiltersSet"
      class="pb-5"
    ></div>

    <v-card-actions>
      <btn
        v-if="isFiltersSet"
        :label="isSelectedNewLog ? 'Create Communication Log' : 'Resend Communication Log'"
        :icon="icons.mdiEmailArrowRight"
        :disabled="!selected.length"
        offline-disable
        color="success"
        @click="openCommModal"
      ></btn>
      <v-spacer></v-spacer>
    </v-card-actions>

    <!-- log detail modal -->
    <v-dialog
      v-model="detailModalState"
      width="550"
      persistent
    >
      <v-card>
        <v-card-title class="pb-0">
          Communication Log Details
        </v-card-title>

        <NoteSection>
          <text-field
            :value="logDetails.log_status"
            label="Communication Log Status"
            class="col-sm-6 mt-3"
            readonly
            @focus="$event.target.blur()"
          ></text-field>
          <text-field
            :value="$date(logDetails.created).format('MM/DD/YYYY hh:mm A')"
            label="Sent On"
            class="col-sm-6 mt-3"
            readonly
            @focus="$event.target.blur()"
          ></text-field>
          <text-field
            :value="$store.getters['facilities/getById'](logDetails.place_of_service_id).title"
            label="Facility"
            class="col-sm-8"
            readonly
            @focus="$event.target.blur()"
          ></text-field>
          <text-field
            :value="$date(logDetails.visit_date).format('MM/DD/YYYY')"
            label="Visit Date"
            class="col-sm-4"
            readonly
            @focus="$event.target.blur()"
          ></text-field>
          <text-field
            :value="logDetails.password"
            label="Password for Communication Log"
            class="col-sm-12"
            readonly
            @focus="$event.target.blur()"
          ></text-field>
          <text-area
            :value="logDetails.contact_email || ' '"
            :no-length="true"
            label="Email Recipients"
            class="col-sm-12"
            readonly
          ></text-area>
          <div class="row-container col-sm-12">
            <v-combobox
              v-model="selectedCountryCode"
              label="Country code"
              :items="countryCodes"
              :item-text="'id'"
              :item-value="'id'"
              class="col-sm-3 country-code"
              :height="72"
              filled
              outlined
              readonly
            ></v-combobox>

            <v-combobox
              ref="mobileNumbersCombobox"
              v-model="communicationLog.mobileNumbers"
              :rules="mobileNumberRules"
              :delimiters="[',', ' ']"
              label="Sms Recipients"
              class="col-sm-9"
              counter="10"
              hide-selected
              outlined
              chips
              deletable-chips
              multiple
            ></v-combobox>
          </div>
          <btn
            :label="sendPasswordLabel"
            color="warning"
            class="col-sm-12"
            :disabled="sendSmsPasswordBtnDisabled"
            :icon="icons.mdiSendCircle"
            @click="sendPasswordThroughSms"
          ></btn>
        </NoteSection>

        <v-card-actions>
          <!-- cancel -->
          <btn
            label="Close"
            color="secondary"
            :icon="icons.mdiClose"
            @click="detailModalState = false; communicationLog.mobileNumbers = []"
          ></btn>
          <v-spacer></v-spacer>
          <btn
            label="Resend Log"
            color="error"
            :icon="icons.mdiEmailArrowRight"
            @click="resendCommunicationLog"
          ></btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- create quick entry modal -->
    <v-dialog
      v-model="createQuickEntryModalState"
      width="900"
      persistent
    >
      <v-card>
        <v-form v-model="formValid">
          <patient-profile-find
            title="Search Patients / Create Quick Entry"
            quick-entry
            :facility="search.facility"
            :modal-state.sync="createQuickEntryModalState"
            @selected="createQuickEntryFromPatient($event)"
          ></patient-profile-find>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- create communication log modal -->
    <v-dialog
      v-model="commModalState"
      width="650"
      persistent
    >
      <v-card>
        <v-form v-model="formValid">
          <v-card-title class="pb-0">
            {{ isSelectedNewLog ? 'Create Communication Log' : 'Resend Communication Log' }}

            <v-spacer></v-spacer>

            <!-- reset -->
            <btn
              label="Reset Values"
              color="secondary"
              :icon="icons.mdiUndoVariant"
              @click="openCommModal"
            ></btn>
          </v-card-title>

          <NoteSection>
            <text-field
              :value="facility.title"
              label="Facility"
              class="col-sm-8 mt-3"
              disabled
            ></text-field>
            <text-field
              :value="$date(search.visit_date).format('MM/DD/YYYY')"
              label="Visit Date"
              class="col-sm-4 mt-3"
              disabled
            ></text-field>
            <text-area
              :value="communicationLog.patients"
              :no-length="true"
              label="Patients Included in Communication Log"
              class="col-sm-12"
              rows="1"
              readonly
              no-click
            ></text-area>
            <text-field
              v-model="communicationLog.password"
              label="Password for Communication Log"
              class="col-sm-7"
              readonly
              password
              :password-visible.sync="communicationLog.isPasswordVisible"
              @focus="$event.target.blur()"
            ></text-field>
            <btn
              label="Generate New Password"
              color="warning"
              class="col-sm-5"
              :icon="icons.mdiLockReset"
              @click="generateNewPassword"
            ></btn>
            <v-combobox
              v-model="communicationLog.recipients"
              :items="communicationLog.emailOptions"
              :rules="computedRules"
              :delimiters="[',', ' ']"
              label="Email Recipients"
              class="col-sm-12"
              counter="10"
              hide-selected
              outlined
              multiple
              chips
              deletable-chips
              @change="commitPendingEmailEntry"
              @blur="commitPendingEmailEntry"
              @update:search-input="emailInput = $event"
              ref="emailCombo"
            ></v-combobox>

            <div class="row-container col-sm-12">
              <v-combobox
                v-model="selectedCountryCode"
                label="Country code"
                :items="countryCodes"
                :item-text="'id'"
                :item-value="'id'"
                class="col-sm-3 country-code"
                :height="72"
                filled
                outlined
                readonly
              ></v-combobox>

              <v-combobox
                ref="mobileNumbersCombobox"
                v-model="communicationLog.mobileNumbers"
                :rules="mobileNumberRules"
                :delimiters="[',', ' ']"
                label="Sms Recipients"
                class="col-sm-9"
                counter="10"
                hide-selected
                outlined
                chips
                deletable-chips
                multiple
              ></v-combobox>
            </div>
            <btn
              :label="sendPasswordLabel"
              color="warning"
              class="col-sm-12"
              :disabled="sendSmsPasswordBtnDisabled"
              :icon="icons.mdiSendCircle"
              @click="sendPasswordThroughSms"
            ></btn>
          </NoteSection>

          <alert class="mx-5">
            <p>
              Are you sure you wish to create the communication log and email it to the listed recipients at {{ facility.title }}?
            </p>
            Warning: This operation cannot be undone!
          </alert>

          <v-card-actions>
            <!-- cancel -->
            <btn
              label="Cancel"
              color="secondary"
              :icon="icons.mdiCancel"
              @click="commModalState = false; communicationLog.isPasswordVisible = false; communicationLog.mobileNumbers = []"
            ></btn>

            <v-spacer></v-spacer>

            <!-- send -->
            <btn
              :label="isSelectedNewLog ? 'Create Log' : 'Resend Log'"
              color="error"
              :icon="icons.mdiEmailArrowRight"
              :disabled="!formValid"
              offline-disable
              @click="createCommunicationLog"
            ></btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- pre generate quick entry -->
    <v-dialog
      v-model="preGenModalState"
      width="1000"
      height="1000"
      persistent
    >
      <QuickEntryPreGeneration>
        <template
          slot="header"
        >
          <!-- cancel -->
          <v-icon
            color="secondary"
            large
            @click="preGenModalState = false"
          >
            {{ icons.mdiClose }}
          </v-icon>
        </template>
      </QuickEntryPreGeneration>
    </v-dialog>

    <div
      v-if="selectedWound"
      class="wound-history-modal"
    >
      <!-- Wound History Modal -->
      <wound-history-modal
        :selected-wound="selectedWound"
        :wound-hx-modal-state.sync="woundHxModalState"
      ></wound-history-modal>
    </div>

    <!-- Location modal -->
    <Location
      v-model="location"
      :wound-id="''"
      :modal-state.sync="locationModal"
    ></Location>
  </v-card>
</template>

<script>
/* eslint-disable no-param-reassign */
import WoundHistoryModal from '@/components/features/WoundHistoryModal.vue'
import QuickEntryPreGeneration from '@/components/notes/encounter/MedEx.vue'
import Location from '@/components/notes/wound-detail/treatments/sections/Location.vue'
import PatientProfileFind from '@/components/patients/Find.vue'
import '@/components/patients/primitives'
import CommunicationLog from '@/mixins/CommunicationLog'
import { createFiltersHandler } from '@/utils/handleSearchFilters'
import {
  mdiAccountClock, mdiAccountEdit,
  mdiAccountEye,
  mdiAccountMultiple,
  mdiAlertDecagram,
  mdiBiohazard,
  mdiCancel,
  mdiCheckCircleOutline,
  mdiCheckDecagram,
  mdiCheckboxBlankOutline,
  mdiCheckboxMultipleBlankOutline,
  mdiCheckboxMultipleMarked,
  mdiChevronDoubleDown,
  mdiChevronDown,
  mdiClipboardAlert,
  mdiClipboardPlusOutline,
  mdiClockPlus,
  mdiClose,
  mdiCloseCircle,
  mdiCloudAlert,
  mdiCloudCheckVariant, mdiCloudOutline,
  mdiContentSaveOutline,
  mdiDecagramOutline,
  mdiDotsCircle,
  mdiDotsVertical,
  mdiEmailAlert,
  mdiEmailArrowRight, mdiEmailCheck,
  mdiEmailOutline,
  mdiEmailSearch,
  mdiHistory,
  mdiInformationOutline,
  mdiLightningBolt,
  mdiLockReset,
  mdiMagnify,
  mdiMinusBoxMultiple,
  mdiSendCircle,
  mdiTableRowPlusAfter, mdiTableRowRemove,
  mdiTimelineQuestion,
  mdiTrashCan,
  mdiUndoVariant,
  mdiCircle,
} from '@mdi/js'
import compact from 'lodash/compact'
import { v4 as uuidv4 } from 'uuid'
import { mapGetters, mapState } from 'vuex'

const initialSearch = {
  last_fourteen_days: false,
  visit_date: null,
  visit_date_us: null,
  facility: null,
  visit_type: null,
  validated: null,
  logStatus: null,
  synced: null,
  ssn_last_4: null,
  first_name: null,
  last_name: null,
  gender: null,
  dob: null,
  dob_us: null,
  // Dummy parameter to allow re-navigating to the same quick-entry page and reload the component.
  pageKey: null,
  patients_ids: [],
}

const initialState = {
  by: 'created',
  desc: true,
}

const quickEntryFiltersHandler = createFiltersHandler('quickEntryFilters', initialSearch)

export default { // Methods: syncEncounters(), syncCorePatients(), syncFullPatients(), syncingProgress()
  components: { PatientProfileFind, WoundHistoryModal, QuickEntryPreGeneration, Location },
  mixins: [CommunicationLog],
  data() {
    return {
      expandRenderKey: 0,
      countryCodes: this.$countryCodes.transformedCountryCodes,
      selectedCountryCode: '(+1) US',
      sendSmsPasswordBtnDisabled: false,
      sendPasswordLabel: 'Send Password',
      woundHxModalState: false,
      selectedWound: {},
      quickEntryEncounters: [],
      patientCache: [],
      expandedWounds: {},
      transitionTrigger: 0,
      transitioned: [],
      dialogVisitDate: null,
      dialogFacility: null,
      measurementMask: [/[0-9|.]/, /[0-9|.]/, /[0-9|.]/, /[0-9|.]/, /[0-9|.]/],
      formValid: false,
      detailModalState: false,
      commModalState: false,
      preGenModalState: false,
      createQuickEntryModalState: false,
      communicationLog: {
        password: null,
        emailOptions: [],
        recipients: [],
        patients: null,
        isPasswordVisible: false,
        mobileNumbers: [],
      },
      logDetails: {},
      logDetailEncounterId: null,
      search: {
        ...initialSearch,
        visit_date: this.$date().format('YYYY-MM-DD'),
      },
      filtered: [],
      selected: [],
      selectAllStatus: false,
      selectAllIndeterminate: false,
      sort: { ...initialState },
      userId: this.$scribeProvider.id ?? this.$authUser.userId(),
      // Use provider's amniotic flag if acting as a scribe, otherwise use the logged-in user's setting
      isUserAmnioEnabled: this.$scribeProvider.id
        ? this.$scribeProvider.isAmnioticEnabled
        : this.$authUser.user().is_amniotic_enabled,
      icons: {
        mdiLightningBolt,
        mdiSendCircle,
        mdiCloseCircle,
        mdiMagnify,
        mdiUndoVariant,
        mdiCheckDecagram,
        mdiEmailArrowRight,
        mdiEmailCheck,
        mdiEmailAlert,
        mdiTimelineQuestion,
        mdiCancel,
        mdiClose,
        mdiDotsVertical,
        mdiAccountClock,
        mdiAccountEdit,
        mdiEmailSearch,
        mdiClockPlus,
        mdiTrashCan,
        mdiEmailOutline,
        mdiDecagramOutline,
        mdiClipboardPlusOutline,
        mdiInformationOutline,
        mdiChevronDown,
        mdiCloudAlert,
        mdiCloudCheckVariant,
        mdiCloudOutline,
        mdiBiohazard,
        mdiAlertDecagram,
        mdiClipboardAlert,
        mdiAccountMultiple,
        mdiLockReset,
        mdiCheckCircleOutline,
        mdiAccountEye,
        mdiCheckboxMultipleBlankOutline,
        mdiCheckboxMultipleMarked,
        mdiMinusBoxMultiple,
        mdiTableRowPlusAfter,
        mdiTableRowRemove,
        mdiChevronDoubleDown,
        mdiContentSaveOutline,
        mdiDotsCircle,
        mdiCheckboxBlankOutline,
        mdiHistory,
        mdiCircle,
      },
      showHelp: false,
      headers: [
        {
          text: 'First',
          value: 'first_name',
          align: 'name',
        },
        {
          text: 'Last',
          value: 'last_name',
          align: 'name',
        },
        {
          text: 'Visit Date',
          value: 'visit_date',
        },
        {
          text: 'Facility',
          value: 'place_of_service_id',
          align: 'facility',
        },
        {
          text: 'Type',
          value: 'visit_type',
          align: 'small',
        },
        {
          text: 'Validated',
          value: 'is_quick_entry_validated',
          align: 'xsmall',
        },
        {
          text: 'Status',
          value: 'communication_log.log_status',
          align: 'xsmall',
        },
        {
          text: 'Synced',
          value: 'is_synced',
          align: 'xsmall',
        },
        {
          value: 'dob',
          align: 'hide',
        },
        {
          value: 'gender',
          align: 'hide',
        },
        {
          value: 'ssn_last_4',
          align: 'hide',
        },
        {
          text: '',
          value: 'actions',
          align: 'xsmall',
          sortable: false,
        },
      ],

      consentOptions: [
        { title: 'Family' },
        { title: 'Patient' },
        { title: 'Patient conservator' },
        { title: 'Patient spouse' },
        { title: 'Two doctors' },
      ],
      woundTypes: [
        { title: 'Wound', id: 1 },
        { title: 'Dermatology', id: 6 },
        { title: 'G-Tube', id: 5 },
      ],
      expanded: [],
      pagination: {
        page: 1,
        pageCount: 0,
        itemsPerPage: 10,
        itemsPerPageList:
          [
            { label: '5', value: 5 },
            { label: '10', value: 10 },
            { label: '15', value: 15 },
            { label: '25', value: 25 },
          ],
      },

      queryDebounceTimer: null,
      encounterDebounceTimer: [],

      // TXP-806. The arguments for each save that is currently sitting in a debounce timer,
      // keyed by encounter id. Without this, beforeDestroy() has no way to run a pending save,
      // because the arguments only exist inside the timer's closure. Anything typed in the final
      // 750 ms before navigating away was previously discarded.
      pendingEncounterSaves: {},

      updateEncounterIndex: 0,
      isUpdateCompleteTimer: null,
      savedTextStatus: false,
      showSaveStatus: false,
      addNewWound: false,
      newWoundPracticeType: null,
      doNotCloseAccordion: false,
      encountersLength: 0,
      isSelectedNewLog: true,
      emailInput: '',
      showAllWoundsMap: {},
      encounterIdCache: {},
      locationModal: false,
      location: {},
      tempLocation: [],
      practiceTypes: [],
      filtersMap: {},
    }
  },
  computed: {
    // TXP-806. Read the persistence-health state so this screen can never claim a save succeeded
    // while writes to this device are actually failing. `mapState` is already imported at the top
    // of this file's <script> block.
    ...mapState({
      saveHealth: state => state.saveHealth,
    }),

    // TXP-806. The single source of truth for whether this screen may display a "Saved"
    // confirmation.
    //
    // Before this change, the indicator was driven entirely by `isUpdateComplete`, which is true
    // when the debounce timer map is empty. That is a statement about a JavaScript timer. It says
    // nothing about IndexedDB. AGENTS.md is explicit that "Vuex is a UI cache layer only" and
    // "MUST NOT be treated as persistent storage" -- so the old indicator was reporting "we
    // updated memory", while the provider read it as "my note is safe on this device".
    canShowSavedStatus() {
      return !(this.saveHealth && this.saveHealth.failed)
    },

    ...mapGetters('baseData', ['query']),
    ...mapGetters('auth', ['facilities']),
    ...mapGetters('encounters', ['woundStages']),
    ...mapGetters('baseData', [
      'woundProcedures',
      'dermProcedures',
      'gtubeProcedures',
      'balloonCc',
      'replacementSize',
    ]),
    ...mapGetters('encounters', [
      'isSynced',
      'genders',
      'visitTypes',
      'logStatus',
      'woundEtiologies',
      'dermEtiologies',
      'infectionSigns',
      'dressings',
      'topicalAgents',
      'abdominalExams',
      'durations',
      'gTubeTypes',
      'periTubeFindings',
      'periTubeTracts',
      'replacementReasons',
      'verificationOfPlacements',
      'lesionConditions',
      'exudateAmounts',
      'exudateTypes',
      'labOptions',
      'progresses'
    ]),
    ...mapState('encounters', { encounters: 'items' }),
    ...mapState('patients', { patients: 'items' }),
    ...mapGetters('patients', [
      'getInsuranceStatusById'
    ]),
    isFiltersSet() {
      return !!this.search.facility && !!this.search.visit_date
    },
    facility() {
      return this.$store.getters['facilities/getById'](this.search.facility)
    },
    computedRules() {
      let rulesArray = []
      rulesArray = rulesArray.concat(this.$validationRules.requiredEmail, this.$validationRules.maxEmails(10))

      return rulesArray
    },
    mobileNumberRules() {
      return this.$validationRules.mobileNumberArrayRequired
    },
    today() {
      return this.$date().format('YYYY-MM-DD')
    },
    allAccordionsClosed() {
      return this.$custom.isEmpty(this.expanded)
    },
    isUpdateComplete() {
      // eslint-disable-next-line no-unused-expressions
      this.updateEncounterIndex
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      const updateStatus = Object.keys(this.encounterDebounceTimer).length === 0

      clearTimeout(this.isUpdateCompleteTimer)
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties, vue/no-async-in-computed-properties
      this.isUpdateCompleteTimer = setTimeout(() => {
        this.savedTextStatus = false
      }, 3000)

      return updateStatus
    },
  },
  watch: {
    'search.last_fourteen_days': {
      handler() {
        if (this.search.last_fourteen_days) this.search.visit_date = null
        else this.search.visit_date = this.$date().format('YYYY-MM-DD')
      }
    },
    'search.facility': {
      handler() {
        this.selectAllStatus = false
        this.selectAllIndeterminate = false
        this.selected = []
      },
    },
    'search.visit_date': {
      handler() {
        this.search.visit_date_us = this.search.visit_date !== null ? this.$date(this.search.visit_date).format('MM/DD/YYYY') : null
        this.selectAllStatus = false
        this.selectAllIndeterminate = false
        this.selected = []
        if (this.search.visit_date && this.search.last_fourteen_days) this.search.last_fourteen_days = false
      },
    },
    'search.dob_us': {
      handler() {
        this.search.dob = this.search.dob_us ? this.$date(this.search.dob_us).format('YYYY-MM-DD') : null
      },
    },
    search: {
      deep: true,
      handler() {
        this.handleSearchChange()
        quickEntryFiltersHandler.saveFilters(this.search)
      },
    },
    pagination: {
      deep: true,
      handler() { this.updateQuery() },
    },
    sort: {
      deep: true,
      handler() { this.updateQuery() },
    },
  },
  beforeMount() {
    this.$store.dispatch('loading', true)

    // Determine if existing query has meaningful filter values
    const hasMeaningfulQuery = () => {
      const q = this.$route.query || {}
      if (this.$custom.isObjectEmpty(q)) return false
      const today = this.$date().format('YYYY-MM-DD')
      return Object.keys(q).some(key => {
        const v = q[key]
        if ([
          'page','pageCount','itemsPerPage','by','desc','pageKey','patients_ids'
        ].includes(key)) return false
        if (v === '' || v === null || typeof v === 'undefined') return false
        if (v === 'false' || v === 'null') return false
        if (key === 'visit_date' && v === today) return false
        return true
      })
    }

    // Load saved filters first if no meaningful query params
    if (!hasMeaningfulQuery()) {
      const saved = quickEntryFiltersHandler.loadFilters()
      if (saved && saved.search) {
        this.search = { ...this.search, ...saved.search }
      }
    }

    this.handleSearchChange()
    this.$store.dispatch('loading', false)
  },
  beforeDestroy() {
    // TXP-806. Best-effort fallback for teardown paths that bypass the router. Normal
    // navigation is handled by beforeRouteLeave(), which can wait for the flush.
    // beforeDestroy() cannot await, so don't rely on this path for guaranteed persistence.
    this.flushPendingEncounterSaves().catch(() => {})
    this.quickEntryEncounters = []
  },
  beforeRouteLeave(to, from, next) {
    // TXP-806. Wait for pending debounced saves before allowing navigation. next() is
    // called even if the flush fails so a persistence problem cannot trap the provider
    // on this screen.
    this.flushPendingEncounterSaves()
      .catch(error => {
        console.error('[quick-entry] flush failed during navigation:', error && error.name)
      })
      .then(() => next())
  },
  mounted() {
    if (!this.$custom.isObjectEmpty(this.$route.query)) {
      const q = this.$route.query
      const today = this.$date().format('YYYY-MM-DD')
      const meaningful = Object.keys(q).some(k => {
        const v = q[k]
        if ([ 'page','pageCount','itemsPerPage','by','desc','pageKey','patients_ids' ].includes(k)) return false
        if (v === '' || v === null || typeof v === 'undefined' || v === 'false' || v === 'null') return false
        if (k === 'visit_date' && v === today) return false
        return true
      })
      if (meaningful) {
        this.search = {
          ...initialSearch,
          ...q,
          facility: Number(q.facility),
          signed: this.$custom.strToBoolNull(q.signed),
          synced: this.$custom.strToBoolNull(q.synced),
          last_fourteen_days: this.$custom.strToBoolNull(q.last_fourteen_days),
        }
        this.sort.by = q.by
        this.sort.desc = this.$custom.strToBoolNull(q.desc)
        if (q.itemsPerPage) this.pagination.itemsPerPage = Number(q.itemsPerPage)
        if (q.page) {
          this.$nextTick(() => { this.pagination.page = Number(q.page) })
        }
      }
    }

    this.loadPracticeTypes()
  },
  methods: {
    getInsuranceStatusClass(patientId) {
      const status = this.getInsuranceStatusById(patientId)

      if (status === 1) {
        return 'insurance-warning'
      }

      if (status === 2) {
        return 'insurance-error'
      }

      if (status === 3) {
        return 'insurance-success'
      }

      return ''
    },
    isEtiologyPressure(treatment) {
      const isPressure = value => {
        const etiology = value?.toLowerCase() || ''

        return /\bpressure\b/i.test(etiology)
          && !/\bnon-pressure\b/i.test(etiology)
      }

      return isPressure(treatment?.etiology)
        || isPressure(treatment?.etiology_other)
    },
    onEtiologyChange(newValue, woundId, treatmentId, item, patient) {
      // Find the wound that contains this treatment
      const wound = patient.wounds.find(w => w.id === woundId)
      if (!wound) return

      // Clear related fields for wound type treatments
      if (wound.practice_type_id === 1) {
        // Find the treatment inside the wound
        const patientTreatment = wound.treatments.find(t => t.id === treatmentId)
        if (!patientTreatment) return

        // Ensure wound_treatment object exists
        if (!patientTreatment.wound_treatment) {
          patientTreatment.wound_treatment = {}
        }
        const wt = patientTreatment.wound_treatment

        // Ensure wound_treatment_wound_size object exists
        if (!wt.wound_treatment_wound_size) {
          wt.wound_treatment_wound_size = {}
        }

        // If the etiology is NOT pressure-related, clear the wound stage
        const value = newValue ? newValue.toString().toLowerCase() : ''
        if (!value.includes('pressure')) {
          wt.wound_treatment_wound_size.wound_stage = null
        }

        // Clear ongoing debridement reason fields since etiology changed
        wt.ongoing_debridement_reason = ''
        wt.ongoing_debridement_reason_other = null
      }

      this.updateEncounterImmediate(item, patient, true)
    },
    getEncounterFilters(encounterId) {
      if (!this.filtersMap[encounterId]) {
        this.$set(this.filtersMap, encounterId, {
          wound: {
            practice_type: [],
            location: '',
            resolved: '',
          }
        })
      }
      return this.filtersMap[encounterId]
    },
    filteredWounds(patientId, encounterId) {
      const patient = this.getPatient(patientId)
      if (!patient || !patient.wounds) return []

      const filters = this.getEncounterFilters(encounterId)

      const results = patient.wounds.filter(wound => {
        if (!this.isShowWound(wound, encounterId) && !this.showAllWoundsMap[encounterId]) {
          return false
        }

        return this.matchesWoundTypeFilter(wound, filters) &&
          this.matchesLocationFilter(wound, filters) &&
          this.matchesResolvedFilter(wound, filters)
      })

      return this.sortWoundsByMostRecentTreatment(results)
    },
    sortWoundsByMostRecentTreatment(wounds) {
      return [...wounds].sort((a, b) => {
        // New wounds first (can_be_deleted = true)
        const aNew = a.can_be_deleted ? 1 : 0
        const bNew = b.can_be_deleted ? 1 : 0
        if (aNew !== bNew) return bNew - aNew

        // Latest treatment date descending
        const dateA = a.latest_treatment_date || ''
        const dateB = b.latest_treatment_date || ''
        if (dateA !== dateB) return dateA > dateB ? -1 : 1

        // Creation date descending
        const createdA = a.created || ''
        const createdB = b.created || ''
        return createdA > createdB ? -1 : 1
      })
    },
    matchesWoundTypeFilter(wound, filters) {
      return !filters.wound.practice_type ||
        filters.wound.practice_type.length === 0 ||
        filters.wound.practice_type.includes(wound.practice_type_id)
    },
    matchesLocationFilter(wound, filters) {
      return !filters.wound.location ||
        (wound?.location?.location_text &&
          typeof wound.location.location_text === 'string' &&
          wound.location.location_text.toLowerCase().includes(
            String(filters.wound.location).toLowerCase()
          ))
    },
    matchesResolvedFilter(wound, filters) {
      return !filters.wound.resolved ||
        (Array.isArray(filters.wound.resolved) && filters.wound.resolved.length === 0) ||
        (Array.isArray(filters.wound.resolved)
          ? filters.wound.resolved.includes(wound?.is_healed ? 'Yes' : 'No')
          : filters.wound.resolved === (wound?.is_healed ? 'Yes' : 'No'))
    },
    loadPracticeTypes() {
      const currentWoundTypes = ['Dermatology', 'Wound', 'G-Tube']

      // eslint-disable-next-line no-confusing-arrow
      this.practiceTypes = this.$store.getters['baseData/practiceTypes'].map(practiceType =>
        currentWoundTypes.includes(practiceType.title) ? practiceType : null)
    },
    getMostRecentTreatmentDate(wound) {
      if (!wound.treatments || wound.treatments.length === 0) return ''

      const activeTreatments = wound.treatments.filter(t => !t.deleted)
      if (activeTreatments.length === 0) return ''

      return activeTreatments.reduce((latest, t) => {
        const visitDate = t.encounter?.visit_date || ''
        return visitDate > latest ? visitDate : latest
      }, '')
    },
    practiceTypeClass(id) {
      switch (id) {
        case 1:
          return 'woundType'
        case 6:
          return 'dermType'
        case 5:
          return 'gtubeType'
        default:
          return ''
      }
    },
    isWoundOrDerm(practiceType) {
      // Wound = 1, Derm = 6
      return practiceType === 1 || practiceType === 6
    },
    waitForLocation() {
      return new Promise(resolve => {
        // Watcher that checks for emitted value from Location component
        const stopWatchLocation = this.$watch('location', (newVal) => {
          if (!this.$custom.isEmpty(newVal)) {
            cleanup()
            this.locationModal = false
            resolve(newVal)
          }
        })

        // Watcher for Location modal open/close state
        const stopWatchModal = this.$watch('locationModal', (isOpen) => {
          if (!isOpen) {
            cleanup()
            resolve(null)
          }
        })

        // Clear location object on cleanup so next time Location
        // component it's opened no value is selected.
        const cleanup = () => {
          stopWatchLocation()
          stopWatchModal()
          // Reset new wound selector
          this.addNewWound = false
          this.newWoundPracticeType = null
          this.location = {}
        }
      })
    },
    toggleShowAllWounds(encounterId, value) {
      this.$set(this.showAllWoundsMap, encounterId, value);
      // Clear filters when showing all wounds
      if (value) {
        this.$set(this.filtersMap, encounterId, {
          wound: {
            practice_type: [],
            location: '',
            resolved: '',
          }
        })
      }
    },
    isShowWound(wound, encounterId) {
      const patientId = wound.patient_id

      // 1. Check for treatments in the current encounter
      const hasCurrentEncounterTreatments = wound?.treatments?.some(treatment => {
        if (treatment.deleted) return false
        return treatment.encounter_id === encounterId
      })

      if (hasCurrentEncounterTreatments) return true

      // 2. Initialize encounterId cache if not present
      if (!this.encounterIdCache) {
        this.encounterIdCache = {}
      }

      // 3. Cache the last signed encounter ID for the patient if not already cached
      if (!this.encounterIdCache[patientId]) {
        const lastSignedEncounterId = this.$store.getters['encounters/lastSignedEncounterId'](patientId)
        this.encounterIdCache[patientId] = lastSignedEncounterId || null
      }

      const lastSignedEncounterId = this.encounterIdCache[patientId]

      // 4. If no signed encounter exists, show the wound by default
      if (!lastSignedEncounterId) return true

      // 5. Check if the wound was treated in the last signed encounter
      return wound.treatments.some(treatment => {
        if (treatment.deleted) return false
        return treatment.encounter_id === lastSignedEncounterId
      })
    },
    commitPendingEmailEntry() {
      const input = this.emailInput?.trim()

      if (input && !this.communicationLog.recipients.includes(input)) {
        this.communicationLog.recipients.push(input)
      }

      this.emailInput = ''
      this.$refs.emailCombo.internalSearch = ''

      // Validate emails
      this.recipientsChanged()
    },
    isPatientValidatedForCommLog(encounter) {
      const patient = this.getPatient(encounter.patient_id)
      if (!patient || !patient.wounds || patient.wounds.length === 0) {
        return false
      }

      const encounterTreatments = []
      patient.wounds.forEach(wound => {
        const treatments = wound.treatments.filter(t => t.encounter_id === encounter.id && !t.deleted)
        treatments.forEach(treatment => {
          encounterTreatments.push({ wound, treatment })
        })
      })

      if (encounterTreatments.length === 0) {
        return false
      }

      // Check that ALL treatments are valid
      return encounterTreatments.every(({ wound, treatment }) => {
        // G-tube wounds are always valid
        if (wound.practice_type_id === 5) {
          return true
        }

        // 1 = Wound practice type, 6 = Derm practice type
        if (wound.practice_type_id !== 1 && wound.practice_type_id !== 6) {
          return false
        }

        const hasLocation = wound.location && wound.location.location_text && wound.location.location_text.trim() !== ''
        const hasEtiology = treatment.etiology && treatment.etiology.trim() !== ''
        const hasEtiologyOther = treatment.etiology !== 'Other' || (treatment.etiology_other && treatment.etiology_other.trim() !== '')

        return hasLocation && hasEtiology && hasEtiologyOther
      })
    },
    disabledAmnioUserDialog() {
      this.$root.confirm({
        titleIconColor: 'error',
        title: 'Approval Required',
        body: 'Access to this procedure must be approved by Skilled Wound Care management.',
        cancel: false,
      })
    },
    async sendPasswordThroughSms() {
      this.$refs.mobileNumbersCombobox.blur()

      await this.$nextTick()

      const isValid = this.$refs.mobileNumbersCombobox.validate()

      if (!isValid) {
        return false
      }

      this.sendSmsPasswordBtnDisabled = true
      this.sendPasswordLabel = 'Sending Password through SMS'
      const foundCountry = this.countryCodes.find(country => country.id === this.selectedCountryCode)
      const payLoadMobileNumbers = []
      this.communicationLog.mobileNumbers.forEach((mobileNumber, index) => {
        payLoadMobileNumbers.push(foundCountry.cleanValue + mobileNumber)
      })

      const password = this.communicationLog.password ? this.communicationLog.password : this.logDetails.password

      const payLoad = {
        to: payLoadMobileNumbers,
        text: `Your communication log password is: ${password}`,
      }

      await this.$store.dispatch('encounters/sendPasswordThroughSms', payLoad)
      this.sendSmsPasswordBtnDisabled = false
      this.sendPasswordLabel = 'Send Password'
    },
    openWoundHxModal(woundObject) {
      this.selectedWound = woundObject
      this.woundHxModalState = true
    },
    getEncountersToSync() {
      return this.encounters.filter(e => (
        e.created_by_user_id === this.userId
        && !e.is_synced
      ))
    },
    getPatientsToSync() {
      return this.patients.filter(p => (
        p.updated
      ))
    },
    isProcedureDebrid(treatment) {
      const procedure = this.$store.getters['baseData/procedureFromId'](treatment.procedure_id)

      return procedure && procedure.toLowerCase().includes('debrid')
    },

    validateProcedure(treatment) {
      this.$nextTick(() => {
        // Clear post measurements if procedure was changed to non-debridement.
        if (!this.isProcedureDebrid(treatment)) {
          treatment.wound_treatment.wound_treatment_wound_size.post_debridement_length = null
          treatment.wound_treatment.wound_treatment_wound_size.post_debridement_width = null
          treatment.wound_treatment.wound_treatment_wound_size.post_debridement_depth = null
          treatment.wound_treatment.wound_treatment_wound_size.post_debridement_percent = null
        }

        // Check if user is allowed to select ATG (178)
        if (!this.isUserAmnioEnabled && treatment.procedure_id === 178) {
          this.disabledAmnioUserDialog()
          treatment.procedure_id = null
        }
      })
    },
    /*areFiltersEmpty() {
      Get default values which are boolean and initialized as false
       const emptyBooleans = Object
        .entries(initialSearch)
        .filter(([_, value]) => value === false)
        .map(([key]) => key)

      const isEmpty = (key, value) => {
        //if (key === 'visit_date' || key === 'visit_date_us') return true

        if (value === null || value === '' || value === undefined || value === 'Invalid Date' || value === 0) return true
        if (value === false && emptyBooleans.includes(key)) return true

        return false
      }

      return Object.keys(initialSearch).every(key => isEmpty(key, this.search[key]))
    }, */
    async handleSearchChange() {
      await this.$store.dispatch('loading', true)
      await this.getQuickEntryRecords()
      await this.$store.dispatch('loading', false)
      this.updateQuery()
    },
    async getQuickEntryRecords() {
      try {
        // Get the quick entry records for this user
        const patients_ids = this.$store.getters['encounters/getPatientsIdFromQuickEntrySearch']({...this.search, user_id: this.userId})
        if (!patients_ids) {
          this.quickEntryEncounters = []
          return
        }

        for (const patient_id of patients_ids) {
          await this.$store.dispatch('encounters/loadEncounterData', patient_id)
          await this.$store.dispatch('patients/loadPatientData', patient_id)
        }

        let quickEntryEncounters = []
        let relatedPatientsIds = []
        if (this.search.gender || this.search.dob || this.search.first_name || this.search.last_name || this.search.ssn_last_4) {
          const matchedIds = this.$store.getters['patients/getPatientsIdsBySearch'](this.search)
          relatedPatientsIds = matchedIds.length ? matchedIds : ['none']
        }

        quickEntryEncounters = this.$store.getters['encounters/filteredQuickEntriesBySearch']({ ...this.search,
          user_id: this.userId,
          patients_ids: relatedPatientsIds,
        })

        this.encountersLength = quickEntryEncounters.length

        if (!this.encountersLength) {
          this.quickEntryEncounters = []
          return
        }

        if (this.encountersLength > 250) {
          this.quickEntryEncounters = []
          return
        }

        // Only cache if count does not exceed 250
        if (this.encountersLength <= 250) {
          this.quickEntryEncounters = this.setEncountersData(quickEntryEncounters)
        }
      } catch (error) {
        await this.$store.dispatch('notify', {
          value: 'Failed to load/set Quick Entries ' + error.message,
          color: 'error'
        })
        this.quickEntryEncounters = []
        await this.$store.dispatch('loading', false)
      }
    },
    setEncountersData(quickEntryEncounters) {
      // Populate possibly missing info from encounters
        quickEntryEncounters
        .forEach(encounter => {
          const patient = this.getPatient(encounter.patient_id)
          encounter.first_name = patient.first_name
          encounter.last_name = patient.last_name
          encounter.gender = patient.gender
          encounter.ssn_last_4 = patient.ssn_last_4
          encounter.dob = patient.dob
          encounter.is_deceased = patient.is_deceased
        })
        quickEntryEncounters
          .filter(x => x.quick_entry_room === undefined
            || x.quick_entry_notes === undefined
            || x.quick_entry_notes_to_nurse === undefined
            || x.labs === undefined
            || x.labs_other === undefined
            || x.labs_order_date === undefined)
          .forEach(encounter => {
            // When adding a new field in QE the changes need to be present here and in Create.vue -> CreateEncounter method
            encounter.quick_entry_room = encounter.quick_entry_room ? encounter.quick_entry_room : ''
            encounter.quick_entry_notes = encounter.quick_entry_notes ? encounter.quick_entry_notes : ''
            encounter.quick_entry_notes_to_nurse = encounter.quick_entry_notes_to_nurse ? encounter.quick_entry_notes_to_nurse : ''
            encounter.labs = encounter.labs || []
            encounter.labs_other = encounter.labs_other || null
            encounter.labs_order_date = encounter.labs_order_date || null
          })

        // Populate possibly missing info from patients/wounds/treatments
        quickEntryEncounters
        .filter(x => x.practice_type_id === 1)
        .forEach(encounter => {
            const patient = this.getPatient(encounter.patient_id)
            patient.wounds.forEach(wound => {
              wound.treatments
                .filter(x => !x.operative_note_wizard)
                .forEach(treatment => {
                  // Only do this for treatments from unsigned encounters. Treatments from signed encounters should not be editable.
                  if (!treatment.encounter.is_signed) {
                    treatment.operative_note_wizard = { consent_selected: null }
                  }
                })
            })
          })

        // Validate quick entry records
        quickEntryEncounters.forEach(encounter => {
          if (!encounter.is_signed) {
            const patient = this.getPatient(encounter.patient_id)
            const validated = this.checkValidation(patient, encounter.id, false)
            if (!validated) {
              encounter.is_quick_entry_validated = false
              if (encounter.is_validated) {
                encounter.is_validated = false
                encounter.is_synced = false
              }
            } else if (validated && !encounter.is_quick_entry_validated) {
              encounter.is_quick_entry_validated = true
            }
          }
        })

        return quickEntryEncounters.sort((a, b) => new Date(b?.created || 0) - new Date(a?.created || 0))

    },
    /**
     * Ensures the tissue percentage value stays within a valid range (0-100).
     * Converts the input to an integer and caps it at a maximum of 100.
     * Returns null if the input is falsy.
     */
    clampTissuePercent(value) {
      return value ? Math.min(parseInt(value), 100) : null
    },
    calcDebridArea(treatment) {
      const woundSize = treatment?.wound_treatment?.wound_treatment_wound_size
      if (woundSize
        && this.$custom.notEmpty(woundSize.post_debridement_length)
        && this.$custom.notEmpty(woundSize.post_debridement_width)
        && this.$custom.notEmpty(woundSize.post_debridement_percent)
      ) {
        woundSize.post_debridement_area_debrided = ((
          woundSize.post_debridement_length
          * woundSize.post_debridement_width
          * woundSize.post_debridement_percent
        ) / 100).toFixed(2)
      }
    },
    setWoundSizePrimaryDepth(treatment, value, item, patient) {
      treatment.wound_treatment.wound_treatment_wound_size = {
        ...treatment.wound_treatment.wound_treatment_wound_size,
        primary_depth: value,
      }
      this.calcWoundSize(treatment, item, patient, true)
    },
    setWoundSizePostDepth(treatment, value, item, patient) {
      treatment.wound_treatment.wound_treatment_wound_size = {
        ...treatment.wound_treatment.wound_treatment_wound_size,
        post_debridement_depth: value,
      }
      this.calcWoundSize(treatment, item, patient, true)
    },
    woundSizePrimaryDepth(treatment) {
      this.$nextTick(() => {
        if (treatment.wound_treatment.wound_treatment_wound_size.primary_depth !== 'UTD') {
          treatment.wound_treatment.wound_treatment_wound_size.primary_depth = treatment.wound_treatment.wound_treatment_wound_size.primary_depth
            ? Number(treatment.wound_treatment.wound_treatment_wound_size.primary_depth || 0).toFixed(1)
            : null
        }
      })
    },
    woundSizePostDepth(treatment) {
      this.$nextTick(() => {
        if (treatment.wound_treatment.wound_treatment_wound_size.post_debridement_depth !== 'UTD') {
          treatment.wound_treatment.wound_treatment_wound_size.post_debridement_depth = treatment.wound_treatment.wound_treatment_wound_size.post_debridement_depth
            ? Number(treatment.wound_treatment.wound_treatment_wound_size.post_debridement_depth || 0).toFixed(1)
            : null
        }
      })
    },
    calcWoundSize(treatment, item, patient, immediate = false) {
      const woundSize = treatment?.wound_treatment?.wound_treatment_wound_size
      if (woundSize) {
        woundSize.is_healed = false
        if (this.$custom.notEmpty(woundSize.primary_length) && this.$custom.notEmpty(woundSize.primary_width)) {
          woundSize.primary_area = (woundSize.primary_length * woundSize.primary_width).toFixed(2)
        }
        if (woundSize.primary_area === '0.00' && this.$custom.notEmpty(woundSize.primary_depth) && Number(woundSize.primary_depth).toFixed(1) === '0.0') {
          woundSize.is_healed = true
          woundSize.wound_size_text = 'Wound resolved'
        } else if (this.$custom.notEmpty(woundSize.primary_length) && this.$custom.notEmpty(woundSize.primary_width) && this.$custom.notEmpty(woundSize.primary_depth) && woundSize.primary_depth === 'UTD') {
          woundSize.wound_size_text = `${Number(woundSize.primary_length).toFixed(1)} cm x ${Number(woundSize.primary_width).toFixed(1)} cm x UTD`
        } else if (this.$custom.notEmpty(woundSize.primary_length) && this.$custom.notEmpty(woundSize.primary_width) && this.$custom.notEmpty(woundSize.primary_depth)) {
          woundSize.wound_size_text = `${Number(woundSize.primary_length).toFixed(1)} cm x ${Number(woundSize.primary_width).toFixed(1)} cm x ${Number(woundSize.primary_depth).toFixed(1)} cm`
        } else if (this.$custom.notEmpty(woundSize.primary_length) && this.$custom.notEmpty(woundSize.primary_width)) {
          woundSize.wound_size_text = `${Number(woundSize.primary_length).toFixed(1)} cm x ${Number(woundSize.primary_width).toFixed(1)} cm`
        } else {
          woundSize.wound_size_text = null
        }
        if (immediate) {
          this.updateEncounterImmediate(item, patient)
        } else {
          this.updateEncounter(item, patient)
        }
      }
    },
    async addWound(encounter, patient, practiceType) {
      let location = {}
      patient.wounds.forEach(w => this.collapseWound(w.id))

      if (this.isWoundOrDerm(practiceType)) {
        this.locationModal = true
        location = await this.waitForLocation()

        if (!location || !location.location_text) {
          return
        }

        // Prevents new wound creation for the same location and practice type
        if (this.checkExistingLocation(location.location_text, practiceType, patient.wounds)) {
          const wound = this.getWoundByLocation(location.location_text, practiceType, patient.wounds)

          if (!wound)
            return this.$store.dispatch('notify', { value: 'No wound could be found for the selected location.', color: 'warning' })


          const hasTreatment = wound.treatments.some(t => !t.deleted && t.encounter_id === encounter.id && t.wound_id === wound.id)

          this.$root.confirm({
            title: 'A wound already exists in this location.',
            body: `<strong>You entered: <span style="color: #d9534f; font-weight: 600;">${location.location_text}</span></strong><br>
                  <br>
                  ${hasTreatment
                    ? 'Would you like to edit the current treatment for the existing wound record?'
                    : 'Would you like to add a treatment to the existing wound?'}`,
            confirm: hasTreatment ? 'Edit Treatment' : 'Add Treatment',
            html: true,
          }).then(async confirm => {
            if (confirm) {
              if (hasTreatment) {
                const treatment = wound.treatments.find(t => !t.deleted
                  && t.encounter_id === encounter.id
                  && wound.id === t.wound_id)

                if (!treatment) return

                if (treatment.etiology === 'Other' && !treatment.etiology_other) {
                  return this.focusOtherEtiology(wound)
                } else if (!treatment.etiology) {
                  return this.focusEtiology(wound)
                } else {
                  setTimeout(() => {
                    this.expandWound(wound.id, true);
                  }, 300);

                  setTimeout(() => {
                    this.scrollToDetails(wound.id, encounter.id);
                  }, 400);
                }
                return
              }


              // No treatment found, we proceed to add one to the wound.
              this.addTreatment(encounter,
                patient,
                wound,
                this.latestTreatment(wound, encounter.visit_date, encounter.id),
                wound.id)

              this.scrollToWound(wound.id, encounter.id)
              this.expandWound(wound.id)
              return
            }
          })
          return
        }
      }

      // Build the start of a new wound
      const woundId = uuidv4()
      const now = this.$custom.utcNow()
      const newWound = {
        // Changes here should also be duplicated in Wounds.vue, method `addWound`
        id: woundId,
        patient_id: patient.id,
        encounter_id: encounter.id,
        provider_user_id: this.userId,
        practice_type_id: practiceType,
        wound_number: patient.wounds?.length > 0
          ? Math.max(...patient.wounds?.map(o => o.wound_number)) + 1
          : 1,
        is_healed: false,
        is_poor_healing: false,
        created: now,
        is_wound_facility_acquired: false,
        is_wound_not_recent: false,
        wound_acquired_facility_id: null,

        // Not in the DB, for Vue only
        place_of_service_id: encounter.place_of_service_id, // I don't believe this is being used, it's not in the DB
        can_be_deleted: true,
        followup_treatment: false,
        updated: true,

        treatments: [{
          id: uuidv4(),
          place_of_service_id: encounter.place_of_service_id,
          patient_id: patient.id,
          encounter_id: encounter.id,
          wound_id: woundId,
          exudate_amounts: [],
          exudate_types: [],
          etiology: null,
          etiology_other: null,
          procedure_id: null,
          current_treatment: null,
          qent_notes: null,
          qent_recommendation: null,
          operative_note: null,
          is_validated: false,
          is_quick_note_validated: practiceType === 5,
          created: now,
          encounter: {
            id: encounter.id,
            visit_date: encounter.visit_date,
          },

          // Not in the DB, for Vue only
          require_vitals: null,
          can_be_deleted: true,
        }],
      }

      // Add specifics for each practice type
      if (practiceType === 1) {
        newWound.location = location
        newWound.treatments[0].wound_treatment = {
          wound_treatment_wound_size: { primary_depth: null },
          qent_undermining: '',
          exudate_amounts: [],
          exudate_types: [],
          wound_description_summary: ''
        }
        newWound.treatments[0].operative_note_wizard = {}
      } else if (practiceType === 6) {
        newWound.location = location
        newWound.treatments[0].derm_treatment = {}
      } else if (practiceType === 5) {
        newWound.treatments[0].g_tube_treatment = {}
      }
      newWound.updated = true

      // Force transition trigger to true before adding the wound
      this.$set(this.transitioned, encounter.id, true)

      // Add the new wound to the patient
      patient.wounds.push(newWound)
      this.$set(patient, 'wounds', [...patient.wounds])

      this.expandRenderKey++
      // If selected, uncheck the communication log checkbox for this encounter
      if (practiceType === 1 || practiceType === 6) {
        this.unselectEncounter(encounter.id)
      }

      await this.$nextTick(() => {
        this.updateEncounterImmediate(encounter, patient, true)

        // Reset add wound dropdown selector
        this.addNewWound = false
        this.newWoundPracticeType = null

        // Expand treatment if g-tube.
        if (practiceType === 5) {
          this.expandWound(woundId)
        }

        practiceType = null
        this.scrollToWound(woundId, encounter.id)
      })
    },
    async addTreatment(encounter, patient, wound, latestTreatment, woundId) {
      if (!this.isNewTreatment(latestTreatment, encounter.id)) {
        // Changes here should also be duplicated in Wounds.vue, method `addTreatment`

        // Check if the wound is healed
        if (wound.is_healed) {
          // Show confirmation dialog
          const result = await this.$root.confirm({
            title: 'Confirm Treatment Addition',
            body: 'This wound is healed. Are you sure you want to add a treatment?',
            confirm: 'Add a treatment',
            cancel: 'Cancel',
          })
          if (!result) {
            return // Exit if the user did not confirm adding treatment to a healed wound.
          }
        }

        // Find a etiology match...
        let etiology = null
        let etiologyOther = null
        if (latestTreatment) {
          etiology = latestTreatment.etiology
          etiologyOther = latestTreatment.etiology_other
          if (latestTreatment.etiology && (wound.practice_type_id === 1 || wound.practice_type_id === 6)) {
            const etiologies = wound.practice_type_id === 1 ? this.woundEtiologies : this.dermEtiologies
            if (etiologies.findIndex(x => x.title === etiology) === -1) {
              const etiologyIndex = etiologies.findIndex(x => x.title === this.$custom.toProperCaseAll(latestTreatment.etiology))
              if (etiologyIndex > -1) {
                etiology = etiologies[etiologyIndex].title
              } else if (etiology && !etiologyOther) {
                etiologyOther = etiology
                etiology = 'Other'
              }
            }
          }
        }

        const newTreatmentId = uuidv4()
        const newTreatment = {
          id: newTreatmentId,
          place_of_service_id: encounter.place_of_service_id,
          patient_id: patient.id,
          encounter_id: encounter.id,
          wound_id: wound.id,
          etiology,
          etiology_other: etiologyOther,
          procedure_id: null,
          current_treatment: null,
          qent_notes: null,
          qent_recommendation: null,
          operative_note: null,
          is_validated: false,
          is_quick_note_validated: false,
          created: this.$custom.utcNow(),
          encounter: {
            id: encounter.id,
            visit_date: encounter.visit_date,
          },

          // Not in the DB, for Vue only
          require_vitals: null,
          can_be_deleted: true,
          updated: true,
        }

        if (wound.practice_type_id === 1) {
          // Wound treatment
          newTreatment.wound_treatment = {
            wound_treatment_wound_size: { primary_depth: null },
            qent_undermining: '',
            wound_description_summary: '',
            exudate_amounts: [],
            exudate_types: [],
          }

          // Pull in consent data from operative note wizard, mainly the consent info
          if (latestTreatment && latestTreatment.operative_note_wizard) {
            newTreatment.operative_note_wizard = {
              consent_selected: latestTreatment.operative_note_wizard?.consent_selected,
              consent_text: latestTreatment.operative_note_wizard.consent_text,
            }
          } else if (!latestTreatment) {
            newTreatment.operative_note_wizard = { consent_selected: null }
          }

          // Wound stage from pervious treatment carries forward
          if (latestTreatment) {
            const woundStage = latestTreatment?.wound_treatment?.wound_treatment_wound_size?.wound_stage
            if (woundStage) {
              newTreatment.wound_treatment.wound_treatment_wound_size.wound_stage = woundStage
            }
          }
        } else if (wound.practice_type_id === 6) {
          // Derm treatment
          newTreatment.derm_treatment = {}
        } else if (wound.practice_type_id === 5) {
          // G-Tube treatment
          newTreatment.g_tube_treatment = {}
        }

        const woundIndex = patient.wounds?.findIndex(x => x.id === woundId)
        patient.wounds[woundIndex].treatments.push(newTreatment)
        patient.wounds[woundIndex].followup_treatment = true // Not in the DB, for Vue only
        patient.wounds[woundIndex].is_healed = false // Mark the wound as not healed if the user adds a treatment
        patient.wounds[woundIndex].updated = true
        latestTreatment = newTreatment
        this.updateEncounterImmediate(encounter, patient, true)

        await this.$nextTick()

        // Only expand if valid (location and etiology are not empty if wound or derm)
        if (newTreatment.is_quick_note_validated) {
          this.expandWound(woundId)
        }

      }
    },
    deleteTreatment(encounter, patient, wound, treatment) {
      const treatmentIndex = wound.treatments.findIndex(x => x.id === treatment.id)
      if (treatmentIndex !== -1) {
        const woundIndex = patient.wounds.findIndex(x => x.id === wound.id)
        const deleteWound = woundIndex !== -1 &&
          wound.can_be_deleted &&
          wound.treatments.filter((_, index) => index !== treatmentIndex).length === 0
        this.$root.confirm({
          title: `Delete ${deleteWound ? 'Wound &' : ''} Treatment Record?`,
          subTitle: 'Warning: This operation cannot be undone!',
          body: `Are you sure you wish to delete this ${deleteWound ? 'wound and' : ' treatment'} record?`,
          confirm: 'Delete',
          confirmIcon: this.icons.mdiTrashCan,
          confirmColor: 'error',
        }).then(result => {
          if (result) {
            // Delete attachments from the patient if there's any related attachment to the wound treatment
            const trt = patient.wounds?.[woundIndex]?.treatments?.find(trt => trt?.wound_id === wound.id)
            const treatmentId = trt?.id ?? null;
            if (treatmentId)
              patient.all_attachments = patient.all_attachments.filter(a => a.treatment_id !== treatmentId)

            if (treatment.can_be_deleted) {
              wound.treatments.splice(treatmentIndex, 1)
              treatment = null
            } else {
              treatment.deleted = true
            }

            if (deleteWound) {
              patient.all_attachments = patient.all_attachments.filter(a => a.wound_id !== wound.id)
              patient.wounds.splice(woundIndex, 1)
              wound = null
            } else {
              this.collapseWound(wound.id)
            }
            this.$nextTick(() => {
              this.updateEncounterImmediate(encounter, patient, true)

              // Force Vue to re-render by toggling addNewWound property
              const prevAddNewWound = this.addNewWound
              this.addNewWound = 0
              this.$nextTick(() => {
                this.addNewWound = prevAddNewWound
              })
            })
          }
        })
      }
    },
    expandWound(id) {
      this.toggleWound(id, true)
    },
    collapseWound(id) {
      this.toggleWound(id, false)
    },
    toggleWound(id, expand = null) {
      if (expand === null) expand = !this.expandedWounds[id]

      // Allow only 1 expanded wound
      if (expand) {
        Object.keys(this.expandedWounds).forEach(wId => {
          if (this.expandedWounds[wId]) {
            this.collapseWound(wId)
          }
        })
      }

      this.$nextTick(() => {
        const woundRef = this.$refs[`wound-${id}`]
        if (woundRef && woundRef[0]) {
          const woundDetails = woundRef[0].querySelector('.wound-container')
          woundRef[0].className = expand ? 'wound expanded' : 'wound'
          if (woundDetails && (this.$custom.isEmpty(woundDetails.style.maxHeight) || woundDetails.style.maxHeight === (expand ? '0px' : 'fit-content'))) {
            woundDetails.setAttribute('style', `max-height: ${woundDetails.scrollHeight}px`)
            if (expand) {
              setTimeout(() => {
                woundDetails.setAttribute('style', 'max-height: fit-content')
                this.expandedWounds[id] = true
              }, 250)
            } else {
              setTimeout(() => {
                woundDetails.setAttribute('style', 'max-height: 0px')
                this.expandedWounds[id] = false
              }, 1)
            }
          }
        }
      })
    },
    toggleAllAccordions(isAllClosed) {
      if (!isAllClosed) {
        // Close all encounters
        this.closeAllAccordions()
      } else {
        // Open all encounters
        this.expanded = this.quickEntryEncounters
        this.$nextTick(() => {
          this.quickEntryEncounters
            .forEach(encounter => {
              this.transitioned[encounter.id] = true
            })
          this.transitionTrigger += 1
        })
        this.showSaveStatus = true
      }
    },
    closeAllAccordions() {
      this.transitioned = []
      this.transitionTrigger += 1
      setTimeout(() => {
        this.expandedWounds = []
        this.addNewWound = false
        this.expanded = []
      }, 250)
    },
    encounterToggleExpand(data) {
      if (data.value) {
        // Encounter is being expanded. Enforce SINGLE expanded row.
        // Collapse any previously expanded encounters (other than the current one)
        const previouslyExpanded = Array.isArray(this.expanded) ? this.expanded : []
        previouslyExpanded
          .filter(e => e.id !== data.item.id)
          .forEach(e => {
            // Remove transition flag
            delete this.transitioned[e.id]
            // Collapse any expanded wounds belonging to the previously expanded encounter
            const prevPatient = this.getPatient(e.patient_id)
            prevPatient.wounds?.forEach(wound => {
              if (this.expandedWounds[wound.id]) this.expandedWounds[wound.id] = false
            })
          })

        // Set the expanded collection to ONLY this encounter
        this.expanded = [data.item]

        this.showSaveStatus = true
        this.$nextTick(() => {
          this.transitioned[data.item.id] = true
          this.transitionTrigger += 1
        })
      } else {
        // Encounter is being collapsed
        delete this.transitioned[data.item.id]
        const patient = this.getPatient(data.item.patient_id)
        patient.wounds?.forEach(wound => {
          // if the wound was expanded, set it to closed
          if (this.expandedWounds[wound.id]) {
            this.expandedWounds[wound.id] = false
          }
        })
        if (this.addNewWound === data.item.id) this.addNewWound = false
        // Remove the encounter from expanded list
        if (Array.isArray(this.expanded)) {
          this.expanded = this.expanded.filter(e => e.id !== data.item.id)
        }
      }
    },
    updateTreatmentsVisitDate(encounter) {
      const patient = this.getPatient(encounter.patient_id)
      patient.wounds?.forEach(wound => {
        const treatment = wound.treatments.filter(x => x.encounter_id === encounter.id && x.deleted !== true)
        if (treatment.length === 1) {
          treatment[0].encounter.visit_date = encounter.visit_date
        }
      })
      this.updateEncounterImmediate(encounter, patient)
    },
    updateTreatmentsFacility(encounter) {
      const patient = this.getPatient(encounter.patient_id)
      patient.wounds?.forEach(wound => {
        const treatment = wound.treatments.filter(x => x.encounter_id === encounter.id && x.deleted !== true)
        if (treatment.length === 1) {
          treatment[0].place_of_service_id = encounter.place_of_service_id
          treatment[0].updated = true
        }
      })
      this.$store.commit('patients/updateFacilityAcquiredWound', {
        id: patient.id,
        facility: encounter.place_of_service_id,
      })
      this.updateEncounterImmediate(encounter, patient)
    },
    showDuplicateWoundLocationDialog(location) {
      return this.$root.confirm({
        title: 'A wound already exists in this location.',
        body: `<strong>You entered: <span style="color: #d9534f; font-weight: 600;">${location}</span></strong><br>The location was not updated.`,
        confirm: 'Ok',
        html: true,
        cancel: false,
      }).then(result => result)
    },
    async updateEncounter(encounter, patient = null, checkValidation = false, wound = null) {
      if (!this.encounterDebounceTimer) this.encounterDebounceTimer = {}

      // Only location passes wound
      if (wound) {
        // Cancel previous debounce
        clearTimeout(this.encounterDebounceTimer[encounter.id])

        await new Promise(resolve => {
          this.encounterDebounceTimer[encounter.id] = setTimeout(async () => {
            const wounds = patient.wounds || []
            const duplicateWound = wounds.find(w =>
              w.practice_type_id === wound.practice_type_id &&
              w.location.location_text?.toLowerCase().trim() === wound.location.location_text?.toLowerCase().trim() &&
              w.id !== wound.id
            )

            if (duplicateWound) {
              const index = wounds.findIndex(w => w.id === wound.id)

              if (index !== -1) {
                await this.showDuplicateWoundLocationDialog(wound.location.location_text)
                const treatment = duplicateWound.treatments.find(t => t?.encounter?.id === encounter.id)

                if (!treatment) {
                  this.scrollToWound(wound.id, encounter.id)
                  return
                }

                if (treatment.etiology === 'Other' && !treatment.etiology_other) {
                  this.focusOtherEtiology(duplicateWound)
                } else if (!treatment.etiology) {
                  this.focusEtiology(duplicateWound)
                } else {
                  this.$nextTick(() => {
                    patient.wounds.forEach(w => this.collapseWound(w.id))
                  })

                  setTimeout(() => {
                    this.expandWound(duplicateWound.id, true);
                  }, 300);

                  setTimeout(() => {
                    this.scrollToDetails(wound.id, encounter.id);
                  }, 500);
                }

                if (this.tempLocation[wound.id]) {
                  patient.wounds[index].location = this.tempLocation[wound.id].full_location
                }

                this.$store.commit('encounters/updateEncounter', { encounter, patient })
              }
            }

            resolve()
          }, 800)
        })
      }

      // Debounce updating encounter/patient
      clearTimeout(this.encounterDebounceTimer[encounter.id])
      this.savedTextStatus = true

      // TXP-806. Record what this pending save would do, so beforeDestroy() can run it if the
      // provider navigates away inside the 750 ms window. The timer's own closure is not reachable
      // from outside, which is why the arguments have to be stored separately.
      this.$set(this.pendingEncounterSaves, encounter.id, {
        encounter, patient, checkValidation, wound,
      })

      this.encounterDebounceTimer[encounter.id] = setTimeout(() => {
        this.updateEncounterImmediate(encounter, patient, checkValidation)
        delete this.encounterDebounceTimer[encounter.id]
        this.$delete(this.pendingEncounterSaves, encounter.id)
        this.saveTempLocation(wound)
      }, 750)
      this.updateEncounterIndex += 1
    },

    // TXP-806. Run every save that is still sitting in a 750 ms debounce timer, right now.
    //
    // Called (and awaited) from beforeRouteLeave() below — the primary path — so that in-app
    // navigation actually waits for the deferred Vuex commit inside updateEncounterImmediate()
    // to land before the route change proceeds. Also called fire-and-forget from beforeDestroy()
    // as a fallback for teardown paths that don't go through the router; beforeDestroy() cannot
    // await, so on that path this is still best-effort.
    //
    // The commit performed by updateEncounterImmediate() goes to the Vuex store, which outlives
    // this component, so it is safe to trigger from either place. The persistence subscriber
    // then writes it to IndexedDB as normal.
    async flushPendingEncounterSaves() {
      const pending = this.pendingEncounterSaves || {}

      for (const encounterId of Object.keys(pending)) {
        const payload = pending[encounterId]

        // Cancel the timer first, so the save cannot also run on its own afterwards.
        clearTimeout(this.encounterDebounceTimer[encounterId])
        delete this.encounterDebounceTimer[encounterId]
        this.$delete(this.pendingEncounterSaves, encounterId)

        if (!payload || !payload.encounter) continue

        try {
          await this.updateEncounterImmediate(payload.encounter, payload.patient, payload.checkValidation)
          this.saveTempLocation(payload.wound)
        } catch (error) {
          // A failure while flushing must never block navigation. Log it and carry on with the
          // remaining encounters. No patient data is logged here.
          console.error('[quick-entry] could not flush a pending save on unmount:', error && error.name)
        }
      }
    },

    saveTempLocation(wound) {
      if (!wound) return // Do not proceed if wound is empty
      this.$set(this.tempLocation, wound.id, {
        location_text: wound.location?.location_text || '',
        full_location: JSON.parse(JSON.stringify(wound.location || {}))
      })
    },
    focusEtiology(wound) {
      const refKey = `etiology-${wound.id}`
      const selectBox = this.$refs[refKey]

      if (!selectBox) {
        return
      }

      const comp = Array.isArray(selectBox) ? selectBox[0] : selectBox
      const innerSelect = comp?.$refs?.thisSelector

      if (!innerSelect) {
        return
      }

      this.$nextTick(() => {
        const el = comp.$el || innerSelect.$el
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }

        setTimeout(() => {
          if (typeof innerSelect.activateMenu === 'function') {
            innerSelect.activateMenu()
          }
        }, 400)
      })
    },
    focusOtherEtiology(wound) {
      const otherRef = `etiology-other-${wound.id}`

      this.$nextTick(() => {
        setTimeout(() => {
          const otherInput = this.$refs[otherRef]
          const comp = Array.isArray(otherInput) ? otherInput[0] : otherInput

          if (comp) {
            if (comp.$el && comp.$el.querySelector) {
              const input = comp.$el.querySelector('input')
              if (input) {
                input.focus()
              }
            }
            else if (comp.focus) {
              comp.focus()
            }
            else if (comp.$refs && comp.$refs.input) {
              comp.$refs.input.focus()
            }
          }
        }, 350)
      })
    },
    scrollToDetails(woundId) {
      if (!this.isShowWound(woundId, encounterId)) return;

      this.$nextTick(() => {
        const el = this.$refs[`wound-details-${woundId}`];
        if (!el) return;

        const target = Array.isArray(el) ? el[0] : el;
        const element = target.$el || target;

        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      });
    },
    scrollToWound(woundId, encounterId) {
      if (!this.isShowWound(woundId, encounterId)) return;

      this.$nextTick(() => {
        const ref = this.$refs[`wound-${woundId}`]
        if (!ref) return

        const target = Array.isArray(ref) ? ref[0] : ref
        if (!target) return

        const element = target.$el || target
        if (!(element instanceof HTMLElement)) return

        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        })
      })
    },
    async updateEncounterImmediate(encounter, patient = null, checkValidation = false) {
      // Wait for things to update first
      await this.$nextTick()

      if (checkValidation && patient) {
        encounter.is_quick_entry_validated = this.checkValidation(patient, encounter.id)
      }

      // Ensure the patient and its wounds exist
      if (patient?.wounds) {
        const woundsToUpdate = new Set()

        // Update treatments and track related wounds
        patient.wounds.forEach(wound => {
          wound.treatments?.forEach(treatment => {
            if (treatment?.encounter?.id === encounter.id) {
              // Flag treatment as unsynced.
              treatment.updated = true
              // Set the treatment as invalid in case the user already validated it on the encounter detail.
              treatment.is_validated = false

              // Clear the "Other Etiology" field if the main etiology is not "Other"
              if (treatment.etiology !== 'Other') treatment.etiology_other = null

              if (treatment?.wound_treatment?.wound_description_summary === null && treatment?.wound_treatment?.wound_description_summary === undefined) {
                treatment = {
                  ...treatment,
                  wound_treatment: {
                    ...treatment.wound_treatment,
                    wound_description_summary: ''
                  }
                }
              }

              // Clear exudate_type if exudate_amount is not selected or if the selection is none
              // Doing it here since iterating twice wounds/treatments is too performance heavy and
              // there's no way to force reactivity when changing it from template
             if (treatment?.wound_treatment && Array.isArray(treatment?.wound_treatment?.exudate_amounts)
              && (!treatment?.wound_treatment?.exudate_amounts[0]
              || treatment?.wound_treatment?.exudate_amounts[0].title === 'None')) {
                treatment.wound_treatment.exudate_types = []
              }

              // Derm cleanups
              const derm = treatment.derm_treatment
              if (derm) {

                // If primary agent is empty → clear its strength and secondary agent + strength
                if (!derm.topical_agent) {
                  derm.topical_agent_strength = null
                  derm.secondary_topical_agent = null
                  derm.secondary_topical_agent_strength = null
                }

                // If secondary agent is empty (but primary exists) → clear only its strength
                else if (!derm.secondary_topical_agent) {
                  derm.secondary_topical_agent_strength = null
                }
              }

              woundsToUpdate.add(treatment.wound_id)
            }
          })
        })

        // Mark related wounds as updated
        patient.wounds.forEach(wound => {
          if (woundsToUpdate.has(wound.id)) {
            wound.updated = true
          }
        })
      }

      this.doNotCloseAccordion = encounter.is_synced
      this.showSaveStatus = true
      this.savedTextStatus = true
      // If acting in Scribe Mode, record the scribe user's ID; otherwise, leave blank
      encounter.modified_by_scribe_id = this.$scribeProvider.id ? this.$authUser.id() : null
      encounter.is_validated = false
      encounter.is_synced = false
      this.$store.commit('encounters/updateEncounter', { encounter, patient })
      if (patient) delete this.patientCache[patient.id]
      this.updateEncounterIndex += 1
    },

    /// ////////////////////////////
    // Validations //

    // Validates quick entry record
    checkValidation(patient, encounterId, unselect = true) {
      if (!patient.wounds.length) {
        if (unselect) this.unselectEncounter(encounterId)

        return false
      }

      let encounterValidated = true
      let woundsWithTreatmentCount = 0

      patient.wounds?.forEach(wound => {
        const treatment = wound.treatments.find(x => x.encounter_id === encounterId && x.deleted !== true)
        if (treatment) {
          woundsWithTreatmentCount += 1
          let treatmentValidated = true

          // Validation for wound and derm treatments
          if ((wound.practice_type_id === 1 || wound.practice_type_id === 6)
            && (!wound.location
              || this.$custom.isEmpty(wound.location.location_text)
              || this.$custom.isEmpty(treatment.etiology)
              || (treatment.etiology === 'Other' && this.$custom.isEmpty(treatment.etiology_other))
            )
          ) {
            treatmentValidated = false
            encounterValidated = false
            this.collapseWound(treatment.wound_id)
          }

          treatment.is_quick_note_validated = treatmentValidated
        }
      })
      const isEntryValid = woundsWithTreatmentCount && encounterValidated
      if (unselect && !isEntryValid) this.unselectEncounter(encounterId)

      return isEntryValid
    },

    // Validates pre debridement wound dimension fields
    validatePrimaryDims(woundTreatmentSize) {
      const length = woundTreatmentSize.primary_length
      const width = woundTreatmentSize.primary_width
      const depth = woundTreatmentSize.primary_depth

      // Returns true when an array of variables is all populated or all blank
      const allTrueOrFalse = arr => arr.every(e => e) || arr.every(e => !e)

      return (!allTrueOrFalse([length, width, depth]))
    },

    // Validates post debridement wound dimension fields
    validatePostDebridDims(woundTreatmentSize) {
      const length = woundTreatmentSize.post_debridement_length
      const width = woundTreatmentSize.post_debridement_width
      const depth = woundTreatmentSize.post_debridement_depth

      // Returns true when an array of variables is all populated or all blank
      const allTrueOrFalse = arr => arr.every(e => e) || arr.every(e => !e)

      return (!allTrueOrFalse([length, width, depth]))
    },

    /// ////////////////////////////

    woundExistsNow(wound, visitDate, encounterId) {
      /*
        If there's no previous or current treatments, but there's
        treatments in the future, this wound didn't exist at this time
      */
      return (this.latestTreatment(wound, visitDate, encounterId)
        || wound.treatments.findIndex(x => !x.deleted && x.encounter?.visit_date > visitDate) === -1
      )
    },
    latestTreatment(wound, visitDate, encounterId) {
      if (!wound.treatments.length) return false

      const treatment = wound.treatments.find(x => x.encounter_id === encounterId && !x.deleted)
      if (treatment) return treatment

      const treatments = wound.treatments
        .filter(x => !x.deleted && x.encounter?.visit_date < visitDate)
        .sort((a, b) => (a.encounter?.visit_date < b.encounter?.visit_date && 1) || -1)

      return (Array.isArray(treatments) && treatments.length) ? treatments[0] : false
    },
    isFirstTreatment(wound) {
      if (!Array.isArray(wound.treatments)) return true

      const hasSignedTreatment = wound.treatments.some(t => {
        if (t.deleted) return false
        return this.$store.getters['encounters/getIsSignedById'](t.encounter_id)
      })

      return !hasSignedTreatment
    },
    isNewTreatment(treatment, encounterId) {
      return (!treatment || !treatment.encounter) ? false : encounterId === treatment.encounter_id
    },
    formattedStatus(treatment) {
      return treatment && treatment.encounter ? this.$date(treatment.encounter.visit_date).format('MM/DD/YYYY') : ''
    },
    formattedLocation(wound) {
      return wound.location ? wound.location.location_text : ''
    },
    formattedEtiology(treatment) {
      if (!treatment) return ''

      const { etiology } = treatment

      return etiology === 'Other' ? `Other - ${treatment.etiology_other}` : etiology
    },
    getFiltered(event) {
      this.filtered = event
      !this.doNotCloseAccordion
        ? this.closeAllAccordions()
        : this.doNotCloseAccordion = false
    },
    selectAllToggle() {
      if (this.selectAllStatus && !this.selectAllIndeterminate) {
        this.selected = this.quickEntryEncounters.filter(
          x => (
            x.is_quick_entry_validated
            || x.is_signed
          ),

          // && (
          //   !x.communication_log
          //   || (x.communication_log.log_status !== 'Initiated' && x.communication_log.log_status !== 'Sent')
          // ),
        )
      } else {
        this.selected = []
      }
      this.$nextTick(() => {
        this.updateSelectStatus()
      })
    },
    updateSelectStatus() {
      const selected = Array.isArray(this.selected) ? this.selected : []
      const entries = Array.isArray(this.quickEntryEncounters) ? this.quickEntryEncounters : []

      const selectedCount = selected.length
      const entryCount = entries.length

      this.selectAllIndeterminate =
        selectedCount > 0 && selectedCount < entryCount

      this.selectAllStatus =
        selectedCount > 0 && selectedCount === entryCount

      this.isSelectedNewLog =
        selectedCount === 0 ||
        selected.some(encounter => !encounter.communication_log_id)
    },
    unselectEncounter(encounterID) {
      if (this.selected.length > 0) {
        const selectedId = this.selected.findIndex(x => x?.id === encounterID)
        if (selectedId > -1) this.selected.splice(selectedId, 1)
      }
    },
    updateQuery() {
      // Debounce query update
      clearTimeout(this.queryDebounceTimer)
      this.queryDebounceTimer = setTimeout(() => {
        // Replace the query
        const query = { ...this.search, ...this.sort, ...this.pagination }
        Object.keys(query).forEach(key => { if (query[key] === null || typeof query[key] === 'undefined') query[key] = '' })
        window.history.replaceState({}, null, `${this.$route.path}?${new URLSearchParams(query).toString()}`)
        this.$store.commit('route/updateQuery', query)
      }, 250)
    },
    resetSearchFilters() {
      this.search = {
        ...initialSearch,
        visit_date: this.$date().format('YYYY-MM-DD'),
      }
      this.sort = { ...initialState }
      this.showHelp = false
    },
    openCommModal() {
      this.communicationLog.isPasswordVisible = false
      this.communicationLog.password = this.facility.password
      this.communicationLog.emailOptions = this.$store.getters['auth/getQuickEntryEmailsById'](this.facility?.id)?.email_addresses?.split(',')
      this.communicationLog.emailOptions?.forEach((email, index) => {
        this.communicationLog.emailOptions[index] = email.trim()
      })
      this.communicationLog.recipients = this.communicationLog.emailOptions

      const patients = []
      this.selected.forEach(patient => {
        patients.push(`${patient.first_name} ${patient.last_name}`)
      })
      this.communicationLog.patients = patients.join(', ')

      this.commModalState = true
    },
    recipientsChanged() {
      let validEmail = true
      this.communicationLog.recipients.forEach((recipient, index) => {
        // Regex to validate email addresses
        // This pattern matches most common email formats, allowing:
        // - Local part: Alphanumeric characters, dots, underscores, plus signs, and hyphens
        // - Domain part: Alphanumeric characters, dots (not consecutive), and hyphens, ending with an alphanumeric character
        // - Top-level domain: At least two alphabetic characters (e.g., .eu, .com, .org)
        const emailPattern = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/

        if (!emailPattern.test(recipient)) {
          this.communicationLog.recipients.splice(index, 1)
          validEmail = false
        }
      })
      if (!validEmail) {
        this.$store.dispatch('notify', { value: 'Invalid email address entered.', color: 'error' })
      }
    },
    viewEncounter(item) {
      // Load the complete data of all encounters related to the patient id.
      this.$store.dispatch('encounters/loadEncounterData', item.patient_id)

      if ((item.is_signed && item.is_synced) || item.is_deferred_care) {
        // View signed surgical note
        this.$router.push({
          name: this.$store.getters['baseData/practiceTypeFromId'](item.practice_type_id).noteRoute,
          query: { id: item.id, quickEntry: true },
        })
      } else if (item.created_by_user_id !== this.userId) {
        // Don't allow a different provider to edit an encounter
        this.$store.dispatch('notify', { value: 'You can\'t edit this encounter as you\'re not the provider who created it.', color: 'error' })
      } else {
        // Edit encounter note
        this.$store.dispatch('loading', true)
        this.$router.push({
          name: this.$store.getters['baseData/practiceTypeFromId'](item.practice_type_id).encounterRoute,
          query: { id: item.id, quickEntry: true },
        })
      }
    },
    createQuickEntryFromEncounter(encounter) {
      // Load the complete data of all encounters related to the patient id.
      this.$store.dispatch('encounters/loadEncounterData', encounter.patient_id)

      this.$router.push({ name: 'create-encounter', query: { id: encounter.patient_id, quickEntry: true }, params: { practiceTypeId: 1, placeOfServiceId: this.facility.id } })
    },
    createQuickEntryFromPatient(patient) {
      // Load the complete data of all encounters related to the patient id.
      this.$store.dispatch('encounters/loadEncounterData', patient.id)

      let facilityId = this.facility.id
      if (patient.place_of_service_id !== this.facility.id) {
        facilityId = patient.place_of_service_id
      }

      this.$router.push({
        name: 'create-encounter',
        query: {
          id: patient.id,
          quickEntry: true,
        },
        params: {
          practiceTypeId: 1,
          placeOfServiceId: facilityId,
        },
      })
    },
    editPatient(item) {
      // Load the complete data of all encounters related to the patient id.
      this.$store.dispatch('encounters/loadEncounterData', item.patient_id)

      this.$router.push({ name: 'edit-patient', query: { id: item.patient_id } })
    },
    communicationLogDetails(item) {
      this.logDetails = item.communication_log
      this.logDetailEncounterId = item.id
      this.detailModalState = true
    },
    createCommunicationLog() {
      this.commModalState = false

      // Filter the selected encounter array to be sure we're sending expected data
      let encountersToSync = this.selected.filter(encounter => (
        encounter.is_quick_entry
        && encounter.is_quick_entry_validated
        && encounter.created_by_user_id === this.userId
        && encounter.place_of_service_id === this.search.facility
        && encounter.visit_date === this.search.visit_date
      ))

      // Build patients to sync
      let patientsToSync = []
      let encounterIds = []
      encountersToSync.forEach(encounter => {
        delete encounter.can_be_deleted
        patientsToSync.push(this.$store.getters['patients/getById'](encounter.patient_id))
        encounterIds.push(encounter.id)
      })

      // Retrieve all unsynced encounters (unrelated to the current comm log) linked to the patient IDs in patientsToSync.
      // We're pushing the entire patient record, including treatments,
      // but some encounters having those new treatments may not be in the communication log.
      patientsToSync.forEach(patient => {
        const otherEncounters = this.encounters.filter(e => (
          e.created_by_user_id === this.userId
          && !e.is_synced
          && e.patient_id === patient.id
        ))

        // Extend encountersToSync array
        otherEncounters.forEach(otherEncounter => {
          encountersToSync.push(otherEncounter)
        })
      })

      // De-dupe arrays
      encountersToSync = [...new Set(compact(encountersToSync))]
      patientsToSync = [...new Set(compact(patientsToSync))]

      // The encounters associated with the communication log,
      // which may not match the total records in encountersToSync
      encounterIds = [...new Set(compact(encounterIds))]

      // Create communication log object
      const commLog = {
        created_by_user_id: this.userId,
        place_of_service_id: this.search.facility,
        visit_date: this.search.visit_date,
        log_type: this.isSelectedNewLog ? 'Initial' : 'Update',
        password: this.communicationLog.password,
        contact_email: this.communicationLog.recipients.join(', '),
        encounter_ids: encounterIds,
      }

      this.sendCommunicationLog(encountersToSync, patientsToSync, commLog)
    },
    generateNewPassword() {
      this.communicationLog.isPasswordVisible = false
      this.$root.confirm({
        title: 'Generate New Communication Log Password?',
        html: true,
        body: `Are you sure you wish to generate a new communication log password for <b>${this.facility.title}</b>?<br><br>
               Note: The new password won't be saved till a new communication log is sent and won't work for previous communication logs.<br><br>`,
        confirm: 'Generate New Password',
        confirmColor: 'error',
        confirmIcon: this.icons.mdiLockReset,
      }).then(result => {
        if (result) {
          // Easily ambiguous numbers/letters removed (O and 0, l and 1, etc.) - 500 billion combinations (29^8)
          const validChars = '23456789abcdefghkmnprstuvwxyz'
          let newPassword = ''
          for (let i = 0; i < 8; i += 1) {
            const randomNumber = Math.floor((crypto.getRandomValues(new Uint32Array(1))[0] / 0x100000000) * validChars.length)
            newPassword += validChars.substring(randomNumber, randomNumber + 1)
          }
          this.communicationLog.isPasswordVisible = true
          this.communicationLog.password = newPassword
          this.$store.dispatch('notify', { value: 'New communication log password generated.' })
        }
      })
    },
    async logCleanup() {
      await this.$store.dispatch('encounters/syncingModal', false)
      this.selected = []
      this.updateSelectStatus()
      this.quickEntryEncounters = []
      await this.getQuickEntryRecords()
    },
    logStatusColor(communicationLog) {
      if (!communicationLog) return 'error'
      switch (communicationLog.log_status) {
        case 'Initiated': return 'warning'
        case 'Sent': return 'success'
        case 'Failed': return 'error'
        default: return 'error'
      }
    },
    logStatusIcon(communicationLog) {
      if (!communicationLog) return this.icons.mdiCloseCircle
      switch (communicationLog.log_status) {
        case 'Initiated': return this.icons.mdiEmailArrowRight
        case 'Sent': return this.icons.mdiEmailCheck
        case 'Failed': return this.icons.mdiEmailAlert
        default: return this.icons.mdiCloseCircle
      }
    },
    canEditEncounter(encounter) {
      return (!encounter.is_signed)
    },
    communicationLogSuccessful(encounter) {
      return encounter.communication_log?.log_status === 'Sent'
    },
    getPatient(patientId) {
      // Load the complete data for the related patient and encounters.
      this.$store.dispatch('patients/loadPatientData', patientId)
      this.$store.dispatch('encounters/loadEncounterData', patientId)

      if (!this.patientCache[patientId]?.is_loaded) {
        this.patientCache[patientId] = this.$store.getters['patients/getById'](patientId)
      }

      return this.patientCache[patientId]
    },
    isExudateAmountEmpty(treatment) {
      if (!treatment?.wound_treatment?.exudate_amounts || treatment.wound_treatment.exudate_amounts[0] === null) return true

      if (Array.isArray(treatment?.wound_treatment?.exudate_amounts) &&
      (treatment?.wound_treatment?.exudate_amounts.length === 0 || treatment?.wound_treatment?.exudate_amounts[0].title === 'None')) return true

      return treatment?.wound_treatment?.exudate_amounts?.title === 'None'
    },
    isOtherSelected(encounter) {
      return encounter?.labs && Array.isArray(encounter.labs) ? encounter.labs.some(x => x.title === 'Other') : false
    },
    handleExudateInput(value, treatment, encounter, patient) {
      this.updateEncounter(encounter, patient)

      if (!value) treatment.wound_treatment.exudate_amounts = []

      if (!Array.isArray(treatment.wound_treatment.exudate_amounts)) {
        treatment.wound_treatment.exudate_amounts = treatment.wound_treatment.exudate_amounts
          ? [value]
          : [];
      }
      treatment = treatment.wound_treatment.exudate_amounts.splice(0, 1, value);
    },
    consentChanged(encounter, patient, treatment) {
      // If consent_text is blank, set consent_text to consent_selected
      if (!treatment.operative_note_wizard.consent_text) {
        this.$nextTick(() => {
          treatment.operative_note_wizard.consent_text = treatment.operative_note_wizard.consent_selected
        })
      } else {
        const consentToReplace = treatment.operative_note_wizard.consent_selected
        this.$nextTick(() => {
          if (this.consentOptions.some(x => treatment.operative_note_wizard.consent_text.includes(x.title))) {
            treatment.operative_note_wizard.consent_text = treatment.operative_note_wizard.consent_text.replace(consentToReplace, treatment.operative_note_wizard.consent_selected || '')
          } else {
            treatment.operative_note_wizard.consent_text = treatment.operative_note_wizard.consent_selected.concat(' ', treatment.operative_note_wizard.consent_text)
          }
        })
      }
      this.updateEncounter(encounter, patient)
    },
    updateWoundProgress(encounter, patient) {
      // Use $nextTick to ensure the v-model has updated before checking the value
      this.$nextTick(() => {
        // Find the treatment
        const treatmentFound = patient.wounds?.find(wound => {
          return wound.treatments.find(treatment => treatment.encounter_id === encounter.id)
        })?.treatments.find(treatment => treatment.encounter_id === encounter.id)

        if (treatmentFound && treatmentFound.wound_treatment) {
          if (treatmentFound.wound_treatment.progress !== 'Other') {
            treatmentFound.wound_treatment.progress_other = ''
          }
        }

        this.updateEncounter(encounter, patient)
      })
    },
    checkExistingLocation(locationText, practiceType, wounds) {
      if (!locationText) return false

      return wounds.some(
        w => w.location?.location_text
          && w.practice_type_id === practiceType
          && w.location.location_text.toLowerCase().trim() === locationText.toLowerCase().trim()
      )
    },
    getWoundByLocation(locationText, practiceType, wounds) {
      if (!locationText) return null
      return wounds.find(
        w => w.practice_type_id === practiceType
        && w.location?.location_text?.toLowerCase().trim() === locationText.toLowerCase().trim()
      )
    },
    async onClickLocation(wound, encounter) {
      this.saveTempLocation(wound)
      this.location = wound.location ? JSON.parse(JSON.stringify(wound.location)) : {}
      this.locationModal = true
      const location = await this.waitForLocation()

      if (location && location.location_text) {
        const treatment = wound.treatments.find(t => !t.deleted && t.encounter_id == encounter.id)

        if (!treatment || !treatment.encounter_id) {
          wound.location = location
          this.location = {}
          return
        }

        if (!encounter) {
          wound.location = location
          this.location = {}
          return
        }

        const patient = this.getPatient(encounter.patient_id)

        const duplicateWound = patient.wounds.find(w =>
          w.practice_type_id === wound.practice_type_id &&
          w.location?.location_text?.toLowerCase().trim() === location.location_text.toLowerCase().trim() &&
          w.id !== wound.id
        )

        if (duplicateWound) {
          if (this.tempLocation[wound.id]) {
            wound.location = this.tempLocation[wound.id].full_location
          }

          await this.showDuplicateWoundLocationDialog(location.location_text)

          const duplicateTreatment = duplicateWound.treatments.find(t =>
            !t.deleted && t.encounter_id === encounter.id
          )

          if (!duplicateTreatment) {
            this.scrollToWound(duplicateWound.id, encounter.id)
            this.location = {}
            return
          }

          if (duplicateTreatment.etiology === 'Other' && !duplicateTreatment.etiology_other) {
            this.focusOtherEtiology(duplicateWound)
          } else if (!duplicateTreatment.etiology) {
            this.focusEtiology(duplicateWound)
          } else {
            this.$nextTick(() => {
              patient.wounds.forEach(w => this.collapseWound(w.id))
            })

            setTimeout(() => {
              this.expandWound(duplicateWound.id, true)
            }, 300)

            setTimeout(() => {
              this.scrollToDetails(duplicateWound.id, encounter.id)
            }, 500)
          }

          this.location = {}
          return
        }

        this.location = {}
        wound.location = location
        this.updateEncounter(encounter, patient, true, wound)

      } else {
        if (this.tempLocation[wound.id]) {
          wound.location = this.tempLocation[wound.id].full_location
        }
        this.location = {}
      }
    }
  },
}
</script>

<style lang="scss">
.insurance-status {
  text-decoration-line: underline;
  text-decoration-thickness: 3px;
  text-underline-offset: 3px;
}

.insurance-warning {
  text-decoration-color: #edb83d;
}

.insurance-error {
  text-decoration-color: #e16155;
}

.insurance-success {
  text-decoration-color: #7dc43c;
}
.practice-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 6px;
  gap: 4px;
  font-size: 14px;
  line-height: 1.2;
  font-weight: bold;
  min-width: 110px !important;
  min-height: 40px !important;
}

/* Wound */
.woundType {
  background-color: rgba(27, 152, 235, 0.3);
  color: rgb(27, 152, 235);
}

/* Derm */
.dermType {
  background-color: rgba(0, 149, 135, 0.3);
  color: rgb(0, 149, 135);
}

/* G-Tube */
.gtubeType {
  background-color: rgba(207, 119, 91, 0.3);
  color: rgb(207, 119, 91);
}


.row-container {
  display: flex;
  align-items: center;
}

.country-code {
  margin-top: -11px !important;

  .v-select__slot {
    max-height: inherit !important;
  }

  .v-input__icon {
    height: 48px !important;
  }
}

.input-flex {
  flex-grow: 1; /* Ensures the inputs grow equally or within their containers */
  margin: 0 5px; /* Adjust margin to provide spacing */
  min-height: 56px;
}

.v-menu__content.v-small-dialog__menu-content {
  margin-top: -12px;
  .v-small-dialog__content {
    padding: 12px 10px 4px 10px;
    max-width: 400px;
  }
}
.quick-entry {
  .v-data-table.accordion-opened tbody tr:not(.v-data-table__expanded__content) {
    background-color: rgba(0, 0, 0, 0.08) !important;
    td {
      color: rgba(94, 86, 105, 0.95) !important;
    }
  }
  .v-data-table tbody tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
    cursor: default;
  }
  .v-input.v-text-field.no-outline {
    input,
    .v-select__selection {
      color: rgba(94, 86, 105, 0.68);
    }
  }
  .v-data-table tbody tr:not(.v-data-table__expanded__content) td {
    input,
    .v-select__selection {
      color: rgb(79, 72, 88) !important;
    }
  }
  .v-data-table__expanded__content td > div .v-input {
    font-weight: 400 !important;
  }
  .text-value {
    margin-top: -12px;
    .no-wrap {
      white-space: nowrap;
      overflow-x: hidden;
      text-overflow: ellipsis;
    }
  }
  .validated-name {
    font-weight: 600 !important;
    color: #4caf50 !important;
  }
  .expanded {
    height: auto !important;
    .wounds-container {
      >.v-card__actions, >.row {
        padding: 9px 3px 4px 3px !important;
        margin: 0 !important;
        align-items: flex-start;
        >div {
          padding: 0 3px;
          .v-btn {
            &.v-btn--block {
              padding: 0;
            }
          }
        }
        .add-wound {
          min-width: unset;
          max-width: 44px;
          margin-left: -3px;
          .v-icon {
            margin: 0 auto;
          }
        }
        &:last-child {
          text-align: center;
        }
      }
    }
    .new-wound {
      margin-bottom: 0 !important;
      .radio-group-container {
        padding: 6px 0 6px 18px;
        >label {
          font-weight: 500;
          color: rgba(94, 86, 105, 0.95);
        }
      }
    }
    // Each wound row
    .wound {
      color: rgba(94, 86, 105, 0.95);
      font-weight: 500 !important;
      margin-bottom: 3px !important;
      border: 1px solid transparent;
      border-bottom-color: rgba(0, 0, 0, 0.06);
      border-radius: 5px;
      max-width: calc(100vw - 3rem);
      transition: margin 0.3s ease, padding 0.3s ease, border 0.3s ease, background-color 0.3s ease;
      &:last-child {
        border-bottom-color: transparent;
      }
      &.expanded {
        border: 1px solid grey;
        padding: 3px 0 3px 0;
        margin: -3px 0 10px 0 !important;
        background-color: rgba(0, 0, 0, 0.04);
        .wound-header {
          .expand-wound {
            transform: rotate(-180deg);
          }
        }
        /*
        .etiology-other {
          max-height: 0;
          min-height: 0;
          padding-top: 0 !important;
          opacity: 0;
          overflow: hidden;
        }
        */
        .wound-details {
          .wound-container {
            opacity: 1;
          }
          .only-comm-log {
            padding: 9px 6px 20px 6px !important;
            >div {
              padding: 9px 3px 4px 3px !important;
              border-style: solid;
              border: 1px solid;
              border-radius: 5px;
            }
          }
        }
      }
      .select-field-label-top, .text-field-label-top, .text-area-label-top {
        .v-input__prepend-outer {
          font-size: 0.85em;
          margin: 0 0 0 2px;
          color: rgba(0, 0, 0, 0.68);
          // The following emulates a small label imbedded in the input box top
          padding: 0 2px;
          top: -5px;
          left: 6px;
          padding-bottom: 1px;
          position: absolute;
          z-index: 2;
          // background-image: linear-gradient(to bottom, transparent, transparent, white, white, white);
          display: inline-block;
          max-width: 86%;
          &::before {
            content: "";
            width: 100%;
            height: 3px;
            z-index: -1;
            background-color: white;
            position: absolute;
            top: 4px;
            left: 0;
          }
        }
      }
      .wound-header {
        align-items: center;
        min-height: 68.5px;
        .expand-wound {
          margin: 0 auto;
          width: 29px;
          height: 29px;
          font-size: 29px;
          svg {
            height: 29px;
          }
        }
        .v-icon:not(.expand-wound) {
          width: 22px;
          height: 22px;
        }
      }
      .etiology-other {
        transition: min-height 0.3s ease, max-height 0.3s ease, padding 0.3s ease, opacity 0.3s ease;
        padding: 6px 5px 0 5px;
        margin: 0 0 0 auto;
        opacity: 1;
        overflow: hidden;
        .v-input__prepend-outer {
          top: 1px;
        }
      }
      .wound-details {
        .wound-container {
          transition: max-height 0.3s ease, opacity 0.3s ease;
          max-height: 0;
          overflow: hidden;
          opacity: 0;
          .hundred-percent {
            width: 694px;
            max-width: 1078px
          }
          .v-input__append-inner {
            padding-right: 2px;
          }
        }
      }
      .v-card__actions, .row {
        padding: 7px 2px 0 2px !important;
        margin: 0 0 -10px 0 !important;
        > *:not(.v-btn) {
          padding: 0 3px 10px 3px !important;
          // line-height: 51px;
          .v-input__slot {
            padding: 0 9px !important;
            label.v-label {
              max-width: 100% !important;
              &.v-label--active {
                max-width: none !important;
              }
            }
          }
          span.v-icon {
            top: -1px;
          }
        }
        .v-btn.del-treatment {
          min-width: unset;
          max-width: 44px;
          margin-top: -6px;
          margin-left: -3px;
          .v-icon {
            margin: 0 auto;
          }
        }
      }
    }
  }
}
.filters-title {
  margin-bottom: 4px;
}
</style>
